/**
 * Copyright (c) 2026 Briq-n-Braq LLC
 * All rights reserved.
 *
 * Proprietary and confidential software.
 * Unauthorized copying, modification, distribution, or use
 * of this software, in whole or in part, is strictly prohibited.
 *
 * KNX Supply Portal — portal.briq-n-braq.com
 * KNX Certified Professional | Partner No. 20248
 */

import { useState, useRef } from "react";
import emailjs from "@emailjs/browser";
import KNXFlowchart from "./KNXFlowchart";
import KNXProductGuide from "./KNXProductGuide";
import KNXTroubleshootingGuide from "./KNXTroubleshootingGuide";
import ETS6QuickReference from "./ETS6QuickReference";
import KNXDiagrams from "./KNXDiagrams";
import KNXProjectCalculator from "./KNXProjectCalculator";

const ACCENT = "#F97316";
const ACCENT_DIM = "#c45a0a";
const ACCENT_LIGHT = "#fed7aa";
const NAVY = "#0f1f3d";
const NAVY_MID = "#1e3560";
const BG = "#f0f4f8";
const SURFACE = "#ffffff";
const SURFACE2 = "#f8fafc";
const BORDER = "#e2e8f0";
const TEXT = "#0f1f3d";
const TEXT_MID = "#64748b";
const TEXT_DIM = "#94a3b8";

const mockOrders = [
  { id: "BNB-0091", customer: "Electro Domo SRL", product: "ABB i-bus KNX IP Router IPR/S 2.1", supplier: "ABB", status: "Shipped", total: "$312.00", date: "May 30", profit: "$74.80" },
  { id: "BNB-0090", customer: "SmartBuild NV", product: "Gira KNX Push-Button 4-gang", supplier: "Gira", status: "Processing", total: "$188.50", date: "May 30", profit: "$42.20" },
  { id: "BNB-0089", customer: "Instatech GmbH", product: "MDT BE-04006.01 Binary Input", supplier: "MDT", status: "Delivered", total: "$97.00", date: "May 29", profit: "$28.10" },
  { id: "BNB-0088", customer: "HomeSys Iberia", product: "Schneider MTN6710-0001 DALI Gateway", supplier: "Schneider", status: "Pending", total: "$445.00", date: "May 29", profit: "$98.90" },
  { id: "BNB-0087", customer: "Lux Integrators", product: "Jung Dimmer Actuator 4CH DA 04 T", supplier: "Jung", status: "Delivered", total: "$263.00", date: "May 28", profit: "$59.40" },
  { id: "BNB-0086", customer: "AutoDom France", product: "Siemens QMX3 Room Sensor", supplier: "Siemens", status: "Shipped", total: "$134.00", date: "May 28", profit: "$31.60" },
];

const mockProducts = [
  // { id: "KNX-201", name: "ABB i-bus KNX IP Router IPR/S 2.1", category: "Interfaces", supplier: "ABB", cost: "$237.20", price: "$312.00", margin: "24.0%", stock: "In Stock", sales: 87, ets: "ETS5+" },
  // { id: "KNX-202", name: "Gira Push-Button 4-gang System 55", category: "Sensors", supplier: "Gira", cost: "$131.90", price: "$188.50", margin: "30.0%", stock: "In Stock", sales: 134, ets: "ETS4+" },
  // { id: "KNX-203", name: "MDT Binary Input 4-fold BE-04006.01", category: "Inputs", supplier: "MDT", cost: "$63.00", price: "$97.00", margin: "35.1%", stock: "Low Stock", sales: 210, ets: "ETS5+" },
  // { id: "KNX-204", name: "Schneider DALI Gateway MTN6710-0001", category: "Gateways", supplier: "Schneider", cost: "$312.00", price: "$445.00", margin: "29.9%", stock: "In Stock", sales: 52, ets: "ETS6" },
  // { id: "KNX-205", name: "Jung Dimmer Actuator 4CH DA 04 T", category: "Actuators", supplier: "Jung", cost: "$184.10", price: "$263.00", margin: "30.0%", stock: "In Stock", sales: 98, ets: "ETS5+" },
  // { id: "KNX-206", name: "Siemens QMX3 Room Temperature Sensor", category: "Sensors", supplier: "Siemens", cost: "$88.40", price: "$134.00", margin: "34.0%", stock: "Out of Stock", sales: 41, ets: "ETS4+" },
  // { id: "KNX-207", name: "Loxone Miniserver Gen 2", category: "Gateways", supplier: "Loxone", cost: "$498.00", price: "$699.00", margin: "28.8%", stock: "In Stock", sales: 73, ets: "ETS5+" },
  // { id: "KNX-208", name: "Loxone KNX Extension", category: "Gateways", supplier: "Loxone", cost: "$214.00", price: "$299.00", margin: "28.4%", stock: "In Stock", sales: 64, ets: "ETS5+" },
  // { id: "KNX-209", name: "Loxone Touch Pure Flex", category: "Sensors", supplier: "Loxone", cost: "$118.00", price: "$169.00", margin: "30.2%", stock: "Low Stock", sales: 91, ets: "ETS6" },
  // { id: "KNX-210", name: "Lutron HomeWorks QSX Processor", category: "Gateways", supplier: "Lutron", cost: "$612.00", price: "$849.00", margin: "27.9%", stock: "In Stock", sales: 38, ets: "—" },
  // { id: "KNX-211", name: "Lutron Palladiom Keypad", category: "Inputs", supplier: "Lutron", cost: "$176.00", price: "$249.00", margin: "29.3%", stock: "In Stock", sales: 57, ets: "—" },
  // { id: "KNX-212", name: "Lutron Sivoia QS Shade Controller", category: "Actuators", supplier: "Lutron", cost: "$284.00", price: "$399.00", margin: "28.8%", stock: "In Stock", sales: 45, ets: "—" },
  // { id: "KNX-213", name: "Ekinex EK-TP5 Touch Panel 5\" KNX", category: "Interfaces", supplier: "Ekinex", cost: "$198.00", price: "$279.00", margin: "29.0%", stock: "In Stock", sales: 29, ets: "ETS5+" },
  // { id: "KNX-214", name: "Ekinex EK-PB4 Push-Button 4-gang KNX", category: "Inputs", supplier: "Ekinex", cost: "$87.00", price: "$124.00", margin: "29.8%", stock: "In Stock", sales: 41, ets: "ETS5+" },
  // ── GVS SMART — KNX HOME & BUILDING CONTROL ──
  // Touch Panels
  { id: "GVS-001", name: "GVS KNX Smart Touch S15 (KNX Secure)", category: "Interfaces", supplier: "GVS Smart", image: "/catalog/gvs-s15.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-002", name: "GVS KNX Smart Touch S13 (KNX Secure)", category: "Interfaces", supplier: "GVS Smart", image: "/catalog/gvs-s13.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-003", name: "GVS KNX Smart Touch S10 (KNX Secure)", category: "Interfaces", supplier: "GVS Smart", image: "/catalog/gvs-s10.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-004", name: "GVS KNX Smart Touch S7 (KNX Secure)", category: "Interfaces", supplier: "GVS Smart", image: "/catalog/gvs-s7.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-005", name: "GVS KNX Smart Touch S4 (KNX/Standard Version)", category: "Interfaces", supplier: "GVS Smart", image: "/catalog/gvs-s4.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-006", name: "GVS KNX Smart Touch S3 (KNX Secure)", category: "Interfaces", supplier: "GVS Smart", image: "/catalog/gvs-s3.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-007", name: "GVS KNX Smart Touch V50s", category: "Interfaces", supplier: "GVS Smart", image: "/catalog/gvs-v50s.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-008", name: "GVS KNX Smart Touch V40s", category: "Interfaces", supplier: "GVS Smart", image: "/catalog/gvs-v40s.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-009", name: "GVS KNX Glass Sensor GS3 Standard Version", category: "Interfaces", supplier: "GVS Smart", image: "/catalog/gvs-gs3.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-010", name: "GVS KNX Glass Sensor GS3 Pro Version", category: "Interfaces", supplier: "GVS Smart", image: "/catalog/gvs-gs3.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },

  // Push Buttons & Input Devices
  { id: "GVS-011", name: "GVS KNX Waltz Touch+ Pad (KNX Secure)", category: "Inputs", supplier: "GVS Smart", image: "/catalog/gvs-waltz-touch-pad.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-012", name: "GVS KNX Waltz Push Button Sensor (KNX Secure)", category: "Inputs", supplier: "GVS Smart", image: "/catalog/gvs-waltz-pb.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-013", name: "GVS KNX S-series Push Button Sensor Display 1-gang (KNX Secure)", category: "Inputs", supplier: "GVS Smart", image: "/catalog/gvs-pb-display-s.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-014", name: "GVS KNX S-series Push Button Sensor Display 2-gang (KNX Secure)", category: "Inputs", supplier: "GVS Smart", image: "/catalog/gvs-pb-display-s.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-015", name: "GVS KNX S-series Push Button Sensor Display 3-gang (KNX Secure)", category: "Inputs", supplier: "GVS Smart", image: "/catalog/gvs-pb-display-s.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-016", name: "GVS KNX Push Button Sensor with Display 55mm 1-gang (KNX Secure)", category: "Inputs", supplier: "GVS Smart", image: "/catalog/gvs-pb-display-55.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-017", name: "GVS KNX Push Button Sensor with Display 55mm 2-gang (KNX Secure)", category: "Inputs", supplier: "GVS Smart", image: "/catalog/gvs-pb-display-55.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-018", name: "GVS KNX Push Button Sensor with Display 55mm 3-gang (KNX Secure)", category: "Inputs", supplier: "GVS Smart", image: "/catalog/gvs-pb-display-55.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-019", name: "GVS KNX Push Button Sensor with Display 55mm 4-gang (KNX Secure)", category: "Inputs", supplier: "GVS Smart", image: "/catalog/gvs-pb-display-55.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-020", name: "GVS KNX Push Button Sensor with LCD 55mm (KNX Secure)", category: "Inputs", supplier: "GVS Smart", image: "/catalog/gvs-pb-lcd-55.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-021", name: "GVS KNX Push Button Sensor Plus 55mm 2-gang (KNX Secure)", category: "Inputs", supplier: "GVS Smart", image: "/catalog/gvs-pb-plus-55.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-022", name: "GVS KNX Push Button Sensor Plus 55mm 3-gang (KNX Secure)", category: "Inputs", supplier: "GVS Smart", image: "/catalog/gvs-pb-plus-55.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-023", name: "GVS KNX Push Button Sensor Plus 55mm 4-gang (KNX Secure)", category: "Inputs", supplier: "GVS Smart", image: "/catalog/gvs-pb-plus-55.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-024", name: "GVS KNX Touch Button Slim 2-Button (KNX Secure)", category: "Inputs", supplier: "GVS Smart", image: "/catalog/gvs-slim.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-025", name: "GVS KNX Touch Button Slim 4-Button (KNX Secure)", category: "Inputs", supplier: "GVS Smart", image: "/catalog/gvs-slim.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-026", name: "GVS KNX Touch Button Slim 6-Button (KNX Secure)", category: "Inputs", supplier: "GVS Smart", image: "/catalog/gvs-slim.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },

  // Sensors
  { id: "GVS-027", name: "GVS KNX Multifunction Sensor 9-in-1 (KNX Secure)", category: "Sensors", supplier: "GVS Smart", image: "/catalog/gvs-multi9.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-028", name: "GVS KNX CO2/Temp/Humidity Sensor 55mm (KNX Secure)", category: "Sensors", supplier: "GVS Smart", image: "/catalog/gvs-co2-55.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-029", name: "GVS KNX Presence Sensor Microwave (KNX Secure)", category: "Sensors", supplier: "GVS Smart", image: "/catalog/gvs-presence-mw.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-030", name: "GVS KNX Motion Sensor PIR (KNX Secure)", category: "Sensors", supplier: "GVS Smart", image: "/catalog/gvs-motion-pir.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-031", name: "GVS KNX Dual Presence Sensor PIR & Ultrasonic (KNX Secure)", category: "Sensors", supplier: "GVS Smart", image: "/catalog/gvs-dual-presence.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-032", name: "GVS KNX GPS Weather Station", category: "Sensors", supplier: "GVS Smart", image: "/catalog/gvs-gps-weather.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-033", name: "GVS KNX GPS Weather Station Pro", category: "Sensors", supplier: "GVS Smart", image: "/catalog/gvs-gps-weather-pro.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },

  // Thermostats
  { id: "GVS-034", name: "GVS KNX S-series Multifunctional Thermostat 6 Buttons (KNX Secure)", category: "Sensors", supplier: "GVS Smart", image: "/catalog/gvs-thermostat-s6.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-035", name: "GVS KNX Thermostat Lite 55mm (KNX Secure)", category: "Sensors", supplier: "GVS Smart", image: "/catalog/gvs-thermostat-lite.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },

  // Actuators
  { id: "GVS-036", name: "GVS KNX Switch Actuator 4-Fold 16A (KNX Secure)", category: "Actuators", supplier: "GVS Smart", image: "/catalog/gvs-switch-act.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-037", name: "GVS KNX Switch Actuator 8-Fold 16A (KNX Secure)", category: "Actuators", supplier: "GVS Smart", image: "/catalog/gvs-switch-act.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-038", name: "GVS KNX Switch Actuator 12-Fold 16A (KNX Secure)", category: "Actuators", supplier: "GVS Smart", image: "/catalog/gvs-switch-act.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-039", name: "GVS KNX Switch Actuator with Energy Detection 3-Fold (KNX Secure)", category: "Actuators", supplier: "GVS Smart", image: "/catalog/gvs-switch-energy.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-040", name: "GVS KNX Switch Actuator with Energy Detection 6-Fold (KNX Secure)", category: "Actuators", supplier: "GVS Smart", image: "/catalog/gvs-switch-energy.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-041", name: "GVS KNX Universal Dimming Actuator 1-Fold Flush Mounted (KNX Secure)", category: "Actuators", supplier: "GVS Smart", image: "/catalog/gvs-dim-1f-flush.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-042", name: "GVS KNX Universal Dimming Actuator 1-Fold DIN Rail (KNX Secure)", category: "Actuators", supplier: "GVS Smart", image: "/catalog/gvs-dim-124.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-043", name: "GVS KNX Universal Dimming Actuator 2-Fold (KNX Secure)", category: "Actuators", supplier: "GVS Smart", image: "/catalog/gvs-dim-124.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-044", name: "GVS KNX Universal Dimming Actuator 4-Fold (KNX Secure)", category: "Actuators", supplier: "GVS Smart", image: "/catalog/gvs-dim-124.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-045", name: "GVS KNX LED Dimming Actuator 4-Fold 4A (KNX Secure)", category: "Actuators", supplier: "GVS Smart", image: "/catalog/gvs-led-dim.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-046", name: "GVS KNX 1-10V Dimming Actuator 4-Fold 16A (KNX Secure)", category: "Actuators", supplier: "GVS Smart", image: "/catalog/gvs-dim10v-4f.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-047", name: "GVS KNX 1-10V Dimming Actuator 3-Fold (KNX Secure)", category: "Actuators", supplier: "GVS Smart", image: "/catalog/gvs-dim10v-3f.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-048", name: "GVS KNX DMX512 Dimming Actuator 4-Fold (KNX Secure)", category: "Actuators", supplier: "GVS Smart", image: "/catalog/gvs-dmx-dim.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-049", name: "GVS KNX Shutter Actuator 4-Fold", category: "Actuators", supplier: "GVS Smart", image: "/catalog/gvs-shutter-4.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-050", name: "GVS KNX Multifunctional Actuator 3-Fold 6A Flush Mounted (KNX Secure)", category: "Actuators", supplier: "GVS Smart", image: "/catalog/gvs-multiact-3.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-051", name: "GVS KNX Multifunctional Actuator 4-Fold 16A (KNX Secure)", category: "Actuators", supplier: "GVS Smart", image: "/catalog/gvs-multiact-4.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-052", name: "GVS KNX Multifunctional Actuator 4/8/16-Fold (KNX Secure)", category: "Actuators", supplier: "GVS Smart", image: "/catalog/gvs-multiact-mega.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-053", name: "GVS KNX Fan Coil Actuator with 0-10V", category: "Actuators", supplier: "GVS Smart", image: "/catalog/gvs-fancoil.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-054", name: "GVS KNX Heating Actuator with Triac 6-Fold (KNX Secure)", category: "Actuators", supplier: "GVS Smart", image: "/catalog/gvs-heating-triac.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },

  // Gateways & Infrastructure
  { id: "GVS-055", name: "GVS KNX/DALI-2 Gateway 1-Fold (KNX Secure)", category: "Gateways", supplier: "GVS Smart", image: "/catalog/gvs-dali-gw.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-056", name: "GVS KNX/DALI-2 Gateway 2-Fold (KNX Secure)", category: "Gateways", supplier: "GVS Smart", image: "/catalog/gvs-dali-gw.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-057", name: "GVS KNX DMX512 Record and Replay 100Min 2-Fold (KNX Secure)", category: "Gateways", supplier: "GVS Smart", image: "/catalog/gvs-dmx-rec.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-058", name: "GVS KNX Gateway for RS485/RS232", category: "Gateways", supplier: "GVS Smart", image: "/catalog/gvs-gw-rs485.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-059", name: "GVS KNX Gateway for Tuya ZigBee Premium", category: "Gateways", supplier: "GVS Smart", image: "/catalog/gvs-gw-tuya.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-060", name: "GVS KNX Multifunctional Gateway", category: "Gateways", supplier: "GVS Smart", image: "/catalog/gvs-gw-multi.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-061", name: "GVS KNX Gateway for IR Ceiling", category: "Gateways", supplier: "GVS Smart", image: "/catalog/gvs-ir-ceiling.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-062", name: "GVS KNX Gateway for IR 2-Fold", category: "Gateways", supplier: "GVS Smart", image: "/catalog/gvs-ir-2f.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-063", name: "GVS KNX Room Controller Smart", category: "Gateways", supplier: "GVS Smart", image: "/catalog/gvs-room-ctrl.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-064", name: "GVS KNX IP Router (KNX Secure)", category: "Gateways", supplier: "GVS Smart", image: "/catalog/gvs-ip-router.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-065", name: "GVS KNX IP Interface (KNX Secure)", category: "Gateways", supplier: "GVS Smart", image: "/catalog/gvs-ip-if.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-066", name: "GVS KNX Line Coupler", category: "Gateways", supplier: "GVS Smart", image: "/catalog/gvs-line-coupler.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-067", name: "GVS KNX USB Interface", category: "Gateways", supplier: "GVS Smart", image: "/catalog/gvs-usb-if.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-068", name: "GVS KNX IR Learner", category: "Gateways", supplier: "GVS Smart", image: "/catalog/gvs-ir-learner.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },

  // Power & Interfaces
  { id: "GVS-069", name: "GVS KNX Power Supply 640mA", category: "Interfaces", supplier: "GVS Smart", image: "/catalog/gvs-power.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-070", name: "GVS KNX Commissioning Device", category: "Interfaces", supplier: "GVS Smart", image: "/catalog/gvs-commissioning.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-071", name: "GVS KNX Universal Interface 4-Fold", category: "Interfaces", supplier: "GVS Smart", image: "/catalog/gvs-uni-if.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-072", name: "GVS KNX Universal Interface with NTC Input 4-Fold (KNX Secure)", category: "Interfaces", supplier: "GVS Smart", image: "/catalog/gvs-uni-if-ntc.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-073", name: "GVS KNX Binary Input 4-Fold (KNX Secure)", category: "Inputs", supplier: "GVS Smart", image: "/catalog/gvs-binary-4f.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
  { id: "GVS-074", name: "GVS KNX Binary Input for Floating Contact 4/8/16-Fold (KNX Secure)", category: "Inputs", supplier: "GVS Smart", image: "/catalog/gvs-binary-float.png", cost: "$0.00", price: "$0.00", margin: "—", stock: "Pending Sample", sales: 0, ets: "ETS6" },
];

const mockSuppliers = [
  // Data per GVS Smart Building Control Systems catalogue (2026.06): GVS Smart Co., Ltd.,
  // Guangzhou, P.R. China — founded 1999, first Chinese KNX member, KNX/CE/RoHS/ISO9001/FCC certified.
  { name: "GVS Smart", country: "🇨🇳 China", products: 74, avgShip: "—", rating: "—", orders: 0, status: "Onboarding", cert: true },
  // { name: "ABB", country: "🇨🇭 Switzerland", products: 38, avgShip: "3-5 days", rating: 4.9, orders: 287, status: "Active", cert: true },
  // { name: "Gira", country: "🇩🇪 Germany", products: 52, avgShip: "4-6 days", rating: 4.8, orders: 341, status: "Active", cert: true },
  // { name: "MDT", country: "🇩🇪 Germany", products: 29, avgShip: "3-4 days", rating: 4.7, orders: 198, status: "Active", cert: false },
  // { name: "Schneider Electric", country: "🇫🇷 France", products: 61, avgShip: "5-7 days", rating: 4.6, orders: 154, status: "Active", cert: true },
  // { name: "Jung", country: "🇩🇪 Germany", products: 44, avgShip: "4-5 days", rating: 4.8, orders: 223, status: "Active", cert: false },
  // { name: "Siemens", country: "🇩🇪 Germany", products: 33, avgShip: "6-8 days", rating: 4.5, orders: 89, status: "Inactive", cert: true },
  // { name: "Loxone", country: "🇦🇹 Austria", products: 27, avgShip: "5-7 days", rating: 4.9, orders: 176, status: "Active", cert: true },
  // { name: "Lutron", country: "🇺🇸 USA", products: 41, avgShip: "2-3 days", rating: 4.8, orders: 142, status: "Active", cert: false },
  // { name: "Ekinex", country: "🇮🇹 Italy", products: 28, avgShip: "5-7 days", rating: 4.8, orders: 54, status: "Active", cert: true },
  // { name: "Inspinia", country: "🇵🇱 Poland", products: 22, avgShip: "4-6 days", rating: 4.6, orders: 31, status: "Active", cert: false },
];

const weeklyData = [14, 19, 23, 17, 31, 38, 44];
const dayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const statusColors = {
  Shipped:         { bg: "#dbeafe", text: "#1e40af" },
  Processing:      { bg: "#fef3c7", text: "#92400e" },
  Delivered:       { bg: "#dcfce7", text: "#166534" },
  Pending:         { bg: "#ffedd5", text: "#9a3412" },
  "Out of Stock":  { bg: "#fee2e2", text: "#991b1b" },
  "Low Stock":     { bg: "#fef3c7", text: "#92400e" },
  "In Stock":      { bg: "#dcfce7", text: "#166534" },
  Active:          { bg: "#dcfce7", text: "#166534" },
  Inactive:        { bg: "#f1f5f9", text: "#94a3b8" },
  Onboarding:      { bg: "#eff6ff", text: "#1d4ed8" },
  "Pending Sample": { bg: "#fef3c7", text: "#92400e" },
};

const categoryColors = {
  Interfaces: { bg: "#dbeafe", text: "#1e40af" },
  Sensors:    { bg: "#dcfce7", text: "#166534" },
  Inputs:     { bg: "#ffedd5", text: "#9a3412" },
  Gateways:   { bg: "#f3e8ff", text: "#6b21a8" },
  Actuators:  { bg: "#cffafe", text: "#155e75" },
};

const Badge = ({ status }) => {
  const c = statusColors[status] || { bg: "#f1f5f9", text: "#64748b" };
  return (
    <span style={{
      background: c.bg, color: c.text,
      padding: "3px 10px", borderRadius: "20px",
      fontSize: "11px", fontWeight: 700,
      letterSpacing: "0.5px", textTransform: "uppercase",
      fontFamily: "monospace", whiteSpace: "nowrap",
    }}>{status}</span>
  );
};

const CatBadge = ({ cat }) => {
  const c = categoryColors[cat] || { bg: "#f1f5f9", text: "#64748b" };
  return (
    <span style={{
      background: c.bg, color: c.text,
      padding: "2px 8px", borderRadius: "4px",
      fontSize: "10px", fontWeight: 700,
      letterSpacing: "0.5px", textTransform: "uppercase",
      fontFamily: "monospace",
    }}>{cat}</span>
  );
};

const MiniChart = ({ data }) => {
  const max = Math.max(...data);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: "5px", height: "52px" }}>
      {data.map((v, i) => (
        <div key={i} style={{ flex: 1 }}>
          <div style={{
            width: "100%",
            height: `${(v / max) * 48}px`,
            background: i === data.length - 1 ? ACCENT : "#cbd5e1",
            borderRadius: "3px 3px 1px 1px",
            border: i === data.length - 1 ? `1px solid ${ACCENT_DIM}` : "1px solid #e2e8f0",
          }} />
        </div>
      ))}
    </div>
  );
};

const fStyle = { width: "100%", boxSizing: "border-box", background: SURFACE2, border: `1px solid ${BORDER}`, borderRadius: "7px", padding: "9px 12px", color: TEXT, fontSize: "13px", outline: "none", fontFamily: "inherit" };
const Lbl = ({ children }) => <label style={{ display: "block", fontSize: "10px", color: TEXT_DIM, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "5px", fontWeight: 600 }}>{children}</label>;
const Grp = ({ label, style, children }) => <div style={{ marginBottom: "14px", ...style }}><Lbl>{label}</Lbl>{children}</div>;
const Row2 = ({ children }) => <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>{children}</div>;
const ModalBtns = ({ onClose }) => (
  <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "22px", paddingTop: "16px", borderTop: `1px solid ${BORDER}` }}>
    <button type="button" onClick={onClose} style={{ background: "none", border: `1px solid ${BORDER}`, borderRadius: "7px", padding: "9px 18px", color: TEXT_MID, cursor: "pointer", fontFamily: "inherit", fontSize: "13px", fontWeight: 600 }}>Cancel</button>
    <button type="submit" style={{ background: ACCENT, border: "none", borderRadius: "7px", padding: "9px 20px", color: "#fff", cursor: "pointer", fontFamily: "inherit", fontSize: "13px", fontWeight: 700 }}>Save</button>
  </div>
);

function Modal({ title, onClose, children }) {
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: "fixed", inset: 0, background: "rgba(15,31,61,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: SURFACE, borderRadius: "12px", width: "460px", maxHeight: "85vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", borderTop: `3px solid ${ACCENT}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "22px 24px 0" }}>
          <div style={{ fontWeight: 800, fontSize: "16px", color: NAVY }}>{title}</div>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", fontSize: "22px", color: TEXT_DIM, cursor: "pointer", lineHeight: 1, padding: "0 4px" }}>×</button>
        </div>
        <div style={{ padding: "18px 24px 24px" }}>{children}</div>
      </div>
    </div>
  );
}

function AddSupplierModal({ onSave, onClose }) {
  const [f, setF] = useState({ name: "", country: "", avgShip: "", products: "", rating: "4.5", status: "Active", cert: false });
  const set = k => e => setF(p => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const submit = e => {
    e.preventDefault();
    onSave({ name: f.name, country: f.country, avgShip: f.avgShip, products: parseInt(f.products) || 0, rating: parseFloat(f.rating) || 0, orders: 0, status: f.status, cert: f.cert });
  };
  return (
    <Modal title="Add Supplier" onClose={onClose}>
      <form onSubmit={submit}>
        <Grp label="Name"><input required style={fStyle} value={f.name} onChange={set("name")} placeholder="e.g. Schneider Electric" /></Grp>
        <Grp label="Country"><input style={fStyle} value={f.country} onChange={set("country")} placeholder="🇩🇪 Germany" /></Grp>
        <Row2>
          <Grp label="Avg. Ship"><input style={fStyle} value={f.avgShip} onChange={set("avgShip")} placeholder="3-5 days" /></Grp>
          <Grp label="Products"><input type="number" min="0" style={fStyle} value={f.products} onChange={set("products")} placeholder="0" /></Grp>
        </Row2>
        <Row2>
          <Grp label="Rating"><input type="number" min="0" max="5" step="0.1" style={fStyle} value={f.rating} onChange={set("rating")} /></Grp>
          <Grp label="Status"><select style={fStyle} value={f.status} onChange={set("status")}><option>Active</option><option>Inactive</option></select></Grp>
        </Row2>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
          <input type="checkbox" id="cert-chk" checked={f.cert} onChange={set("cert")} style={{ width: "16px", height: "16px", accentColor: ACCENT }} />
          <label htmlFor="cert-chk" style={{ fontSize: "13px", color: TEXT, cursor: "pointer" }}>KNX Certified</label>
        </div>
        <ModalBtns onClose={onClose} />
      </form>
    </Modal>
  );
}

function AddProductModal({ supplierNames, onSave, onClose }) {
  const cats = ["Interfaces", "Sensors", "Inputs", "Gateways", "Actuators"];
  const [f, setF] = useState({ name: "", category: "Interfaces", supplier: supplierNames[0] || "", ets: "", cost: "", price: "", stock: "In Stock" });
  const set = k => e => setF(p => ({ ...p, [k]: e.target.value }));
  const submit = e => {
    e.preventDefault();
    const cost = f.cost.startsWith("$") ? f.cost : `$${parseFloat(f.cost).toFixed(2)}`;
    const price = f.price.startsWith("$") ? f.price : `$${parseFloat(f.price).toFixed(2)}`;
    const cn = parseFloat(cost.replace("$", "")) || 0;
    const pn = parseFloat(price.replace("$", "")) || 1;
    onSave({ name: f.name, category: f.category, supplier: f.supplier, ets: f.ets || "—", cost, price, margin: `${((pn - cn) / pn * 100).toFixed(1)}%`, stock: f.stock, sales: 0 });
  };
  return (
    <Modal title="Add Product" onClose={onClose}>
      <form onSubmit={submit}>
        <Grp label="Product Name"><input required style={fStyle} value={f.name} onChange={set("name")} placeholder="e.g. ABB i-bus KNX IP Router" /></Grp>
        <Row2>
          <Grp label="Category"><select style={fStyle} value={f.category} onChange={set("category")}>{cats.map(c => <option key={c}>{c}</option>)}</select></Grp>
          <Grp label="Supplier"><select style={fStyle} value={f.supplier} onChange={set("supplier")}>{supplierNames.map(s => <option key={s}>{s}</option>)}</select></Grp>
        </Row2>
        <Row2>
          <Grp label="Cost ($)"><input style={fStyle} value={f.cost} onChange={set("cost")} placeholder="237.20" /></Grp>
          <Grp label="Price ($)"><input style={fStyle} value={f.price} onChange={set("price")} placeholder="312.00" /></Grp>
        </Row2>
        <Row2>
          <Grp label="ETS Compatibility"><input style={fStyle} value={f.ets} onChange={set("ets")} placeholder="ETS5+" /></Grp>
          <Grp label="Stock"><select style={fStyle} value={f.stock} onChange={set("stock")}><option>In Stock</option><option>Low Stock</option><option>Out of Stock</option></select></Grp>
        </Row2>
        <ModalBtns onClose={onClose} />
      </form>
    </Modal>
  );
}

function AddOrderModal({ productList, onSave, onClose }) {
  const [f, setF] = useState({ customer: "", product: productList[0]?.name || "", status: "Pending", total: "", profit: "" });
  const set = k => e => setF(p => ({ ...p, [k]: e.target.value }));
  const submit = e => {
    e.preventDefault();
    const total = f.total.startsWith("$") ? f.total : `$${parseFloat(f.total).toFixed(2)}`;
    const profit = f.profit.startsWith("$") ? f.profit : `$${parseFloat(f.profit).toFixed(2)}`;
    onSave({ customer: f.customer, product: f.product, status: f.status, total, profit });
  };
  return (
    <Modal title="New Order" onClose={onClose}>
      <form onSubmit={submit}>
        <Grp label="Customer"><input required style={fStyle} value={f.customer} onChange={set("customer")} placeholder="e.g. Electro Domo SRL" /></Grp>
        <Grp label="Product">
          <select style={fStyle} value={f.product} onChange={set("product")}>
            {productList.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
          </select>
        </Grp>
        <Row2>
          <Grp label="Status"><select style={fStyle} value={f.status} onChange={set("status")}>{["Pending","Processing","Shipped","Delivered"].map(s => <option key={s}>{s}</option>)}</select></Grp>
          <Grp label="Total ($)"><input style={fStyle} value={f.total} onChange={set("total")} placeholder="312.00" /></Grp>
        </Row2>
        <Grp label="Profit ($)"><input style={fStyle} value={f.profit} onChange={set("profit")} placeholder="74.80" /></Grp>
        <ModalBtns onClose={onClose} />
      </form>
    </Modal>
  );
}

const PUBLIC_RESOURCES = [
  { id: "flowchart",    label: "Installation Flowchart",  icon: "◈", tag: "Planning",     desc: "4-phase KNX installation workflow with technical notes and per-step commissioning guidance." },
  { id: "guide",        label: "Product Selection Guide", icon: "⬡", tag: "Specification", desc: "5-question wizard that outputs a tailored KNX product stack with estimated component counts." },
  { id: "troubleshoot", label: "Troubleshooting Guide",   icon: "◆", tag: "Diagnostics",   desc: "Interactive fault diagnosis for 6 common KNX issues — 50 step-by-step diagnostic checks." },
  { id: "ets6ref",      label: "ETS6 Quick Reference",    icon: "▦", tag: "Reference",     desc: "Shortcuts, DPT table, address formats, bus diagnostics and commissioning checklist in one card." },
  { id: "diagrams",     label: "System Diagrams",         icon: "🏗️", tag: "Reference",     desc: "Visual KNX system architecture and wiring diagrams." },
  { id: "calculator",   label: "Project Calculator",      icon: "🧮", tag: "Planning",      desc: "3-step wizard that sizes PSUs and couplers and outputs a KNX bill of materials." },
];

const RESOURCE_COMPONENTS = {
  flowchart:    KNXFlowchart,
  guide:        KNXProductGuide,
  troubleshoot: KNXTroubleshootingGuide,
  ets6ref:      ETS6QuickReference,
  diagrams:     KNXDiagrams,
  calculator:   KNXProjectCalculator,
};

export default function BriqNBraqPortalLight() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [productSearch, setProductSearch] = useState("");
  const [productCategory, setProductCategory] = useState("All");
  const [currency, setCurrency] = useState("USD");
  const [orders, setOrders] = useState(mockOrders);
  const [products, setProducts] = useState(mockProducts);
  const [suppliers, setSuppliers] = useState(mockSuppliers);
  const [modal, setModal] = useState(null);
  const [resourcesPage, setResourcesPage] = useState("flowchart");
  const [authed, setAuthed] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [loginError, setLoginError] = useState("");
  const [showLanding, setShowLanding] = useState(true);
  const [publicResource, setPublicResource] = useState(null);
  const [publicProducts, setPublicProducts] = useState(false);
  const [publicAbout, setPublicAbout] = useState(false);
  const [reqForm, setReqForm] = useState({ firstName: "", lastName: "", company: "", email: "", country: "", knxCert: null, message: "" });
  const [reqStatus, setReqStatus] = useState("idle"); // idle | sending | success | error
  const [lightboxImg, setLightboxImg] = useState(null);
  const reqFormRef = useRef(null);

  const fmt = (usdStr) => {
    const n = parseFloat(String(usdStr).replace(/[$,]/g, ""));
    if (isNaN(n)) return usdStr;
    const val = currency === "EUR" ? n * 0.92 : n;
    const sym = currency === "EUR" ? "€" : "$";
    return sym + val.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const saveSupplier = (data) => { setSuppliers(prev => [data, ...prev]); setModal(null); };
  const saveProduct  = (data) => {
    const nums = products.map(p => parseInt(p.id.replace(/\D/g, ""), 10)).filter(Number.isFinite);
    const nextId = `GVS-${String((nums.length ? Math.max(...nums) : 0) + 1).padStart(3, "0")}`;
    setProducts(prev => [{ id: nextId, ...data }, ...prev]);
    setModal(null);
  };
  const saveOrder = (data) => {
    const nextId = `BNB-${String(Math.max(...orders.map(o => parseInt(o.id.replace("BNB-", "")))) + 1).padStart(4, "0")}`;
    const date = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const prod = products.find(p => p.name === data.product);
    setOrders(prev => [{ id: nextId, ...data, supplier: prod?.supplier || "", date }, ...prev]);
    setModal(null);
  };

  const productCategories = ["All", "Interfaces", "Sensors", "Inputs", "Gateways", "Actuators"];
  const productQ = productSearch.toLowerCase();
  const filteredProducts = products.filter(p =>
    (productCategory === "All" || p.category === productCategory) &&
    (!productQ || p.name.toLowerCase().includes(productQ) || p.supplier.toLowerCase().includes(productQ) || p.id.toLowerCase().includes(productQ))
  );

  const renderDemoBanner = (text) => (
    <div style={{ background: SURFACE2, border: `1px solid ${BORDER}`, borderLeft: `3px solid ${ACCENT}`, borderRadius: "6px", padding: "7px 12px", marginTop: "12px", fontSize: "11.5px", color: TEXT_DIM, lineHeight: 1.5 }}>
      {text}
    </div>
  );
  const DEMO_TEXT_DASHBOARD = "📋 Demo Mode — Dashboard metrics, orders and analytics shown are illustrative. Live data will populate as orders are placed.";
  const DEMO_TEXT_SHORT = "📋 Demo Mode — Data shown is illustrative. Live data will populate as orders are placed.";

  const goToRequestAccess = () => {
    setPublicProducts(false);
    setPublicAbout(false);
    setTimeout(() => document.getElementById("bnb-request-access")?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  const lightboxEl = lightboxImg && (
        <div
          onClick={() => setLightboxImg(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.75)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "zoom-out",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: "12px",
              padding: "16px", maxWidth: "90vw", maxHeight: "90vh",
              display: "flex", flexDirection: "column", alignItems: "center", gap: "12px",
              boxShadow: "0 25px 60px rgba(0,0,0,0.4)",
            }}
          >
            <img
              src={lightboxImg}
              alt="Product"
              style={{ maxWidth: "70vw", maxHeight: "75vh", objectFit: "contain", borderRadius: "8px" }}
            />
            <button
              onClick={() => setLightboxImg(null)}
              style={{
                background: "#0f1f3d", color: "#fff", border: "none",
                borderRadius: "6px", padding: "8px 24px",
                fontSize: "13px", fontWeight: 700, cursor: "pointer"
              }}
            >Close ✕</button>
          </div>
        </div>
  );

  const renderProducts = (isPublic) => (
            <div>
            {isPublic && (
              <div style={{ background: NAVY, borderLeft: `4px solid ${ACCENT}`, borderRadius: "8px", padding: "10px 16px", marginBottom: "16px", fontSize: "12.5px", fontWeight: 600, color: "#ffffff", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px 24px" }}>
                <span>Browse our KNX catalog —</span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 24px" }}>
                <button onClick={goToRequestAccess} style={{ background: "none", border: "none", padding: 0, color: "#ffffff", font: "inherit", fontWeight: 600, textDecoration: "underline", textDecorationColor: ACCENT, textUnderlineOffset: "3px", cursor: "pointer" }}>New here? Request portal access</button>
                <button onClick={() => { setPublicProducts(false); setShowLanding(false); }} style={{ background: "none", border: "none", padding: 0, color: ACCENT, font: "inherit", fontWeight: 700, textDecoration: "underline", textUnderlineOffset: "3px", cursor: "pointer" }}>Already a member? Sign in</button>
                </div>
              </div>
            )}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "18px" }}>
                <div>
                  <h1 style={{ fontSize: "20px", fontWeight: 900, color: TEXT, margin: 0 }}>Products</h1>
                  <p style={{ color: TEXT_DIM, fontSize: "12px", margin: "4px 0 0" }}>{isPublic ? "KNX component catalog" : "KNX component catalog & pricing"}</p>
                </div>
                {!isPublic && <button onClick={() => setModal("product")} style={{ background: ACCENT, border: "none", borderRadius: "8px", color: "#fff", fontWeight: 700, fontSize: "13px", padding: "9px 18px", cursor: "pointer" }}>+ Add Product</button>}
              </div>

              {/* Filter bar */}
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px", flexWrap: "wrap" }}>
                <div style={{ position: "relative", flexShrink: 0 }}>
                  <span style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: TEXT_DIM, fontSize: "12px", pointerEvents: "none" }}>⌕</span>
                  <input type="text" placeholder="Search name, supplier, ref…" value={productSearch}
                    onChange={e => setProductSearch(e.target.value)}
                    style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: "7px", padding: "7px 12px 7px 28px", color: TEXT, fontSize: "12px", outline: "none", fontFamily: "inherit", width: "220px" }} />
                </div>
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  {productCategories.map(cat => {
                    const active = productCategory === cat;
                    const cc = categoryColors[cat];
                    return (
                      <button key={cat} onClick={() => setProductCategory(cat)} style={{
                        padding: "5px 12px", borderRadius: "20px", cursor: "pointer",
                        fontSize: "10px", fontWeight: 700, letterSpacing: "0.5px",
                        fontFamily: "monospace", textTransform: "uppercase", border: "1px solid",
                        background: active ? (cc ? cc.bg : "#ffedd5") : "transparent",
                        color: active ? (cc ? cc.text : ACCENT_DIM) : TEXT_DIM,
                        borderColor: active ? (cc ? cc.text : ACCENT_DIM) : BORDER,
                        transition: "all 0.12s",
                      }}>{cat}</button>
                    );
                  })}
                </div>
                {(productSearch || productCategory !== "All") && (
                  <span style={{ fontSize: "11px", color: TEXT_DIM }}>
                    {filteredProducts.length} of {products.length}
                    <button onClick={() => { setProductSearch(""); setProductCategory("All"); }} style={{ background: "none", border: "none", color: ACCENT_DIM, cursor: "pointer", fontSize: "11px", marginLeft: "8px", padding: 0 }}>✕ clear</button>
                  </span>
                )}
              </div>

              <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: "10px", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${BORDER}`, background: SURFACE2 }}>
                      {(isPublic
                        ? ["Image", "Ref", "Product Name", "Category", "Supplier", "ETS", "Price", "Stock"]
                        : ["Image", "Ref", "Product Name", "Category", "Supplier", "ETS", "Cost", "Price", "Margin", "Stock", "Sales"]
                      ).map(h => (
                        <th key={h} style={{ textAlign: "left", padding: "13px 12px", fontSize: "9px", color: TEXT_DIM, textTransform: "uppercase", letterSpacing: "1px", fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.length > 0 ? filteredProducts.map((p, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${BORDER}`, cursor: "pointer" }}
                        onMouseEnter={e => e.currentTarget.style.background = SURFACE2}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <td style={{ padding: "8px 12px" }}>
                          {p.image
                            ? <img src={p.image} alt={p.name} loading="lazy" onClick={() => setLightboxImg(p.image)} style={{ width: "46px", height: "46px", objectFit: "contain", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: "6px", display: "block", cursor: "zoom-in" }} />
                            : <div style={{ width: "46px", height: "46px", background: SURFACE2, border: `1px solid ${BORDER}`, borderRadius: "6px" }} />}
                        </td>
                        <td style={{ padding: "13px 12px", fontSize: "10px", color: TEXT_DIM, fontFamily: "monospace" }}>{p.id}</td>
                        <td style={{ padding: "13px 12px", fontSize: "12px", fontWeight: 700, color: TEXT, maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</td>
                        <td style={{ padding: "13px 12px" }}><CatBadge cat={p.category} /></td>
                        <td style={{ padding: "13px 12px", fontSize: "11px", color: TEXT_MID }}>{isPublic ? "KNX Certified Partner" : p.supplier}</td>
                        <td style={{ padding: "13px 12px", fontSize: "10px", color: "#1e40af", fontFamily: "monospace", fontWeight: 700 }}>{p.ets}</td>
                        {isPublic ? (
                          <>
                            <td style={{ padding: "13px 12px" }}>
                              <button onClick={goToRequestAccess} style={{ background: ACCENT, color: "#fff", border: "none", borderRadius: "20px", padding: "4px 12px", fontSize: "10px", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", cursor: "pointer", whiteSpace: "nowrap" }}>Request Quote</button>
                            </td>
                            <td style={{ padding: "13px 12px" }}><Badge status={p.stock} /></td>
                          </>
                        ) : (
                          <>
                            <td style={{ padding: "13px 12px", fontSize: "12px", color: TEXT_MID }}>{fmt(p.cost)}</td>
                            <td style={{ padding: "13px 12px", fontSize: "12px", fontWeight: 700, color: NAVY }}>{fmt(p.price)}</td>
                            <td style={{ padding: "13px 12px", fontSize: "12px", color: "#16a34a", fontWeight: 700 }}>{p.margin}</td>
                            <td style={{ padding: "13px 12px" }}><Badge status={p.stock} /></td>
                            <td style={{ padding: "13px 12px", fontSize: "12px", fontWeight: 600, color: ACCENT_DIM }}>{p.sales}</td>
                          </>
                        )}
                      </tr>
                    )) : (
                      <tr><td colSpan={isPublic ? 8 : 11} style={{ padding: "40px", textAlign: "center", color: TEXT_DIM, fontSize: "13px" }}>No products match your search.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
  );

  const navItems = [
    { id: "dashboard", icon: "▦", label: "Dashboard" },
    { id: "orders",    icon: "◈", label: "Orders" },
    { id: "products",  icon: "⬡", label: "Products" },
    { id: "suppliers", icon: "◉", label: "Suppliers" },
    { id: "analytics", icon: "◎", label: "Analytics" },
    { id: "resources", icon: "◇", label: "Resources" },
    { id: "about",     icon: "◎", label: "About" },
  ];

  const stats = [
    { label: "Revenue (May)", value: "$24,680", usd: true,  delta: "+22.1%", up: true,  sub: "vs April" },
    { label: "KNX Orders",    value: "183",      usd: false, delta: "+31",    up: true,  sub: "this month" },
    { label: "Avg. Margin",   value: "30.4%",    usd: false, delta: "+1.8%",  up: true,  sub: "vs last month" },
    { label: "Pending",       value: "8",        usd: false, delta: "-4",     up: false, sub: "need action" },
  ];

  function handleLogin(e) {
    e.preventDefault();
    if (loginEmail.trim() === "admin@briq-n-braq.com" && loginPass === "BnB2026!") {
      setAuthed(true);
      setLoginError("");
    } else {
      setLoginError("Invalid email or password.");
    }
  }

  /* ── Public about page ── */
  if (showLanding && publicAbout) {
    const aboutCards = [
      { icon: "◉", title: "KNX Certified Expertise", body: "Partner No. 20248. Every product recommendation backed by certified KNX knowledge." },
      { icon: "⬡", title: "EU & North America", body: "Deep roots in the European KNX ecosystem with on-the-ground presence in North America." },
      { icon: "◇", title: "Direct Manufacturer Sourcing", body: "Briq-n-Braq works with leading KNX manufacturers to source and supply components to professional installers across EU and North America." },
    ];
    return (
      <div style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif", background: "#f0f4f8", color: "#0f1f3d", minHeight: "100vh" }}>
        <div style={{
          position: "sticky", top: 0, zIndex: 200,
          background: "#162d52", borderBottom: `2px solid ${ACCENT}`,
          padding: "0 24px", height: "46px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <button
              onClick={() => setPublicAbout(false)}
              style={{ background: "none", border: "none", color: "#cbd5e1", fontSize: "13px", fontWeight: 600, cursor: "pointer", padding: 0 }}
            >← Back to Home</button>
            <span style={{ color: "#334155", fontSize: "13px" }}>|</span>
            <span style={{ color: ACCENT, fontSize: "12.5px", fontWeight: 600 }}>About</span>
          </div>
          <button
            onClick={() => { setPublicAbout(false); setShowLanding(false); }}
            style={{ background: ACCENT, color: "#fff", border: "none", borderRadius: "6px", padding: "5px 14px", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}
          >Sign In to Portal →</button>
        </div>

        {/* Hero */}
        <section style={{ background: "#0f1f3d", padding: "72px 6%", textAlign: "center", borderBottom: `3px solid ${ACCENT}` }}>
          <h1 style={{ margin: "0 auto 16px", maxWidth: "820px", fontSize: "clamp(30px, 5vw, 46px)", fontWeight: 900, color: "#ffffff", letterSpacing: "-1px", lineHeight: 1.12 }}>
            Bridging European KNX Technology <span style={{ color: ACCENT }}>with North America</span>
          </h1>
          <p style={{ margin: "0 auto", maxWidth: "620px", fontSize: "17px", color: "#94a3b8", lineHeight: 1.65 }}>
            A specialist KNX supply portal for professional installers and system integrators
          </p>
        </section>

        {/* Our Story */}
        <section style={{ padding: "64px 6% 24px" }}>
          <div style={{ maxWidth: "820px", margin: "0 auto" }}>
            <h2 style={{ margin: "0 0 16px", fontSize: "24px", fontWeight: 900, color: "#0f1f3d" }}>Our Story</h2>
            <p style={{ margin: "0 0 14px", fontSize: "15.5px", color: "#475569", lineHeight: 1.8 }}>
              As a European-trained KNX Certified Professional (Partner No. 20248) now based in the United States, Briq-n-Braq was founded to bridge the gap between established European KNX technology and the growing North American smart building market.
            </p>
            <p style={{ margin: 0, fontSize: "15.5px", color: "#475569", lineHeight: 1.8 }}>
              We serve professional KNX installers and system integrators across the EU and North America, providing access to premium KNX components from leading certified manufacturers — backed by real technical expertise.
            </p>
          </div>
        </section>

        {/* What We Do */}
        <section style={{ padding: "40px 6%" }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <h2 style={{ margin: "0 0 20px", fontSize: "24px", fontWeight: 900, color: "#0f1f3d", textAlign: "center" }}>What We Do</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "18px" }}>
              {aboutCards.map(c => (
                <div key={c.title} style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderTop: `3px solid ${ACCENT}`, borderRadius: "12px", padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                  <div style={{ fontSize: "22px", color: ACCENT, marginBottom: "10px" }}>{c.icon}</div>
                  <h3 style={{ margin: "0 0 8px", fontSize: "16px", fontWeight: 800, color: "#0f1f3d" }}>{c.title}</h3>
                  <p style={{ margin: 0, fontSize: "14px", color: "#64748b", lineHeight: 1.7 }}>{c.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Briq-n-Braq */}
        <section style={{ padding: "24px 6% 40px" }}>
          <div style={{ maxWidth: "820px", margin: "0 auto", background: "#ffffff", border: "1px solid #e2e8f0", borderLeft: `4px solid ${ACCENT}`, borderRadius: "10px", padding: "28px 32px" }}>
            <h2 style={{ margin: "0 0 12px", fontSize: "22px", fontWeight: 900, color: "#0f1f3d" }}>Why Briq-n-Braq</h2>
            <p style={{ margin: 0, fontSize: "15.5px", color: "#475569", lineHeight: 1.8 }}>
              North America is significantly underserved for KNX. While Europe has thousands of certified installers and established supply chains, North American professionals face limited access to quality KNX components and local technical support. Briq-n-Braq exists to change that.
            </p>
          </div>
        </section>

        {/* KNX Standard */}
        <section style={{ background: "#0f1f3d", padding: "56px 6%", textAlign: "center" }}>
          <div style={{ maxWidth: "820px", margin: "0 auto" }}>
            <h2 style={{ margin: "0 0 14px", fontSize: "22px", fontWeight: 900, color: ACCENT }}>The KNX Standard</h2>
            <p style={{ margin: "0 0 10px", fontSize: "16px", color: "#cbd5e1", lineHeight: 1.8 }}>
              KNX is the world's only open standard for building automation — backed by over 500 manufacturers and 100,000+ certified professionals globally.
            </p>
            <p style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: "#ffffff", letterSpacing: "0.02em" }}>
              IEC 14543-3 certified. Vendor neutral. Future proof.
            </p>
          </div>
        </section>

        {/* CTAs */}
        <section style={{ padding: "56px 6%", textAlign: "center" }}>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={() => { setPublicAbout(false); setPublicProducts(true); }}
              style={{ background: "#0f1f3d", color: ACCENT, border: `2px solid ${ACCENT}`, borderRadius: "9px", padding: "14px 30px", fontSize: "15px", fontWeight: 700, cursor: "pointer" }}
            >Browse Products</button>
            <button
              onClick={goToRequestAccess}
              style={{ background: ACCENT, color: "#fff", border: "none", borderRadius: "9px", padding: "14px 30px", fontSize: "15px", fontWeight: 700, cursor: "pointer" }}
            >Request Portal Access →</button>
          </div>
        </section>
      </div>
    );
  }

  /* ── Public products catalog ── */
  if (showLanding && publicProducts) {
    return (
      <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: "#f0f4f8", minHeight: "100vh" }}>
        <div style={{
          position: "sticky", top: 0, zIndex: 200,
          background: "#162d52", borderBottom: `2px solid ${ACCENT}`,
          padding: "0 24px", height: "46px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <button
              onClick={() => setPublicProducts(false)}
              style={{ background: "none", border: "none", color: "#cbd5e1", fontSize: "13px", fontWeight: 600, cursor: "pointer", padding: 0 }}
            >← Back to Home</button>
            <span style={{ color: "#334155", fontSize: "13px" }}>|</span>
            <span style={{ color: ACCENT, fontSize: "12.5px", fontWeight: 600 }}>Products</span>
          </div>
          <button
            onClick={() => { setPublicProducts(false); setShowLanding(false); }}
            style={{ background: ACCENT, color: "#fff", border: "none", borderRadius: "6px", padding: "5px 14px", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}
          >Sign In to Portal →</button>
        </div>
        <div style={{ padding: "24px 6%" }}>
          {renderProducts(true)}
        </div>
        {lightboxEl}
      </div>
    );
  }

  /* ── Public resource viewer ── */
  if (showLanding && publicResource) {
    const ResComp = RESOURCE_COMPONENTS[publicResource];
    const resLabel = PUBLIC_RESOURCES.find(r => r.id === publicResource)?.label || "";
    return (
      <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: NAVY, minHeight: "100vh" }}>
        <div style={{
          position: "sticky", top: 0, zIndex: 200,
          background: "#162d52", borderBottom: `2px solid ${ACCENT}`,
          padding: "0 24px", height: "46px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <button
              onClick={() => setPublicResource(null)}
              style={{ background: "none", border: "none", color: "#cbd5e1", fontSize: "13px", fontWeight: 600, cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: "5px" }}
            >{publicResource === "calculator" ? "← Back to Home" : "← Back"}</button>
            <span style={{ color: "#334155", fontSize: "13px" }}>|</span>
            <span style={{ color: ACCENT, fontSize: "12.5px", fontWeight: 600 }}>{resLabel}</span>
          </div>
          <button
            onClick={() => { setPublicResource(null); setShowLanding(false); }}
            style={{ background: ACCENT, color: "#fff", border: "none", borderRadius: "6px", padding: "5px 14px", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}
          >Sign In to Portal →</button>
        </div>
        <ResComp />
      </div>
    );
  }

  /* ── Public landing page ── */
  if (showLanding) {
    return (
      <div style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif", background: "#f0f4f8", color: "#0f1f3d", minHeight: "100vh" }}>

        {/* Nav */}
        <nav style={{
          position: "sticky", top: 0, zIndex: 100,
          background: "#0f1f3d", borderBottom: "1px solid #1e3a6e",
          padding: "0 6%", height: "58px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ background: ACCENT, color: "#fff", fontWeight: 900, fontSize: "15px", padding: "4px 9px", borderRadius: "6px", letterSpacing: "-0.4px" }}>BB</div>
            <span style={{ color: "#ffffff", fontWeight: 800, fontSize: "15px" }}>Briq-n-Braq</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <a href="https://briq-n-braq.com" style={{ color: "#94a3b8", fontSize: "13px", textDecoration: "none" }}>briq-n-braq.com</a>
            <button
              onClick={() => setPublicAbout(true)}
              style={{ background: "transparent", color: ACCENT, border: `1px solid ${ACCENT}`, borderRadius: "7px", padding: "7px 16px", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}
            >About</button>
            <button
              onClick={() => setPublicProducts(true)}
              style={{ background: "transparent", color: ACCENT, border: `1px solid ${ACCENT}`, borderRadius: "7px", padding: "7px 16px", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}
            >Products</button>
            <button
              onClick={() => setShowLanding(false)}
              style={{ background: ACCENT, color: "#fff", border: "none", borderRadius: "7px", padding: "8px 18px", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}
            >Sign In to Portal</button>
          </div>
        </nav>

        {/* Hero */}
        <section style={{ background: "radial-gradient(ellipse at center, #eef2ff 0%, #ffffff 70%)", padding: "88px 6% 80px", textAlign: "center" }}>
          <div style={{ display: "inline-block", background: `${ACCENT}20`, border: `1px solid ${ACCENT}50`, color: ACCENT, fontSize: "11px", fontWeight: 700, padding: "4px 14px", borderRadius: "20px", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "24px" }}>
            KNX Certified Partner No. 20248
          </div>
          <h1 style={{ margin: "0 0 20px", fontSize: "clamp(38px, 6vw, 58px)", fontWeight: 900, color: "#0f1f3d", letterSpacing: "-1.5px", lineHeight: 1.08 }}>
            Smart Parts.<br /><span style={{ color: ACCENT }}>Fast Ship.</span>
          </h1>
          <p style={{ margin: "0 auto 36px", maxWidth: "560px", fontSize: "18px", color: "#64748b", lineHeight: 1.7 }}>
            Premium KNX automation components for professional installers across EU and North America.
          </p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={() => setShowLanding(false)}
              style={{ background: ACCENT, color: "#fff", border: "none", borderRadius: "9px", padding: "14px 30px", fontSize: "15px", fontWeight: 700, cursor: "pointer" }}
            >Sign In to Portal →</button>
            <button
              onClick={() => document.getElementById("bnb-resources")?.scrollIntoView({ behavior: "smooth" })}
              style={{ background: "transparent", color: "#0f1f3d", border: "2px solid #0f1f3d", borderRadius: "9px", padding: "14px 30px", fontSize: "15px", fontWeight: 600, cursor: "pointer" }}
            >Explore Free Tools ↓</button>
            <button
              onClick={() => setPublicResource("calculator")}
              style={{ background: "#0f1f3d", color: ACCENT, border: `2px solid ${ACCENT}`, borderRadius: "9px", padding: "14px 30px", fontSize: "15px", fontWeight: 700, cursor: "pointer" }}
            >🧮 KNX Project Calculator</button>
            <button
              onClick={() => setPublicProducts(true)}
              style={{ background: "#0f1f3d", color: ACCENT, border: `2px solid ${ACCENT}`, borderRadius: "9px", padding: "14px 30px", fontSize: "15px", fontWeight: 700, cursor: "pointer" }}
            >Products</button>
          </div>
        </section>

        {/* Feature cards */}
        <section style={{ background: "#0f1f3d", padding: "64px 6%" }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "18px" }}>
            {[
              { icon: "◉", title: "KNX Certified Expertise",   sub: "Partner No. 20248",            body: "Every product recommendation and project query is handled by a KNX-certified professional with hands-on commissioning experience in residential and commercial installations." },
              { icon: "⬡", title: "EU & North America",         sub: "Global reach, local knowledge", body: "Deep roots in the European KNX ecosystem with an on-the-ground presence in North America. We understand the code requirements, voltages, and market dynamics of both regions." },
              { icon: "◇", title: "Zero-Inventory Drop-Ship",   sub: "Direct from manufacturer",      body: "No warehouse overhead. Orders ship direct from certified KNX manufacturers to your project site, keeping costs lean and lead times predictable for every order." },
            ].map((card, i) => (
              <div key={i} style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "24px 22px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                  <div style={{ width: "36px", height: "36px", borderRadius: "9px", background: `${ACCENT}20`, border: `1px solid ${ACCENT}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "17px", color: ACCENT, flexShrink: 0 }}>{card.icon}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "14px", color: "#0f1f3d", lineHeight: 1.2 }}>{card.title}</div>
                    <div style={{ fontSize: "11px", color: ACCENT, fontWeight: 600, marginTop: "2px" }}>{card.sub}</div>
                  </div>
                </div>
                <p style={{ margin: 0, fontSize: "13px", color: "#64748b", lineHeight: 1.7 }}>{card.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Resources */}
        <section id="bnb-resources" style={{ background: "#f8fafc", padding: "72px 6%" }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: "40px" }}>
              <div style={{ display: "inline-block", background: `${ACCENT}18`, border: `1px solid ${ACCENT}44`, color: ACCENT, fontSize: "11px", fontWeight: 700, padding: "3px 12px", borderRadius: "20px", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "14px" }}>Free — No Login Required</div>
              <h2 style={{ margin: "0 0 10px", fontSize: "28px", fontWeight: 900, color: "#0f1f3d", letterSpacing: "-0.5px" }}>KNX Professional Tools</h2>
              <p style={{ margin: 0, fontSize: "15px", color: "#64748b" }}>Open-access resources for KNX installers and system integrators</p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
              {PUBLIC_RESOURCES.map(r => (
                <button
                  key={r.id}
                  onClick={() => setPublicResource(r.id)}
                  style={{ textAlign: "left", background: "#ffffff", border: "1.5px solid #e2e8f0", borderRadius: "12px", padding: "22px 24px", cursor: "pointer", transition: "border-color 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = ACCENT}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "#e2e8f0"}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                    <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: `${ACCENT}20`, border: `1px solid ${ACCENT}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "17px", color: ACCENT, flexShrink: 0 }}>{r.icon}</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: "14px", color: "#0f1f3d", lineHeight: 1.2 }}>{r.label}</div>
                      <div style={{ fontSize: "10px", color: ACCENT, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: "2px" }}>{r.tag}</div>
                    </div>
                    <div style={{ marginLeft: "auto", color: "#475569", fontSize: "16px", flexShrink: 0 }}>→</div>
                  </div>
                  <p style={{ margin: 0, fontSize: "13px", color: "#64748b", lineHeight: 1.6 }}>{r.desc}</p>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* About */}
        <section style={{ background: "#0f1f3d", padding: "72px 6%", borderTop: "1px solid #1e3a6e" }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "64px", alignItems: "center" }}>
            <div>
              <div style={{ display: "inline-block", background: `${ACCENT}18`, border: `1px solid ${ACCENT}44`, color: ACCENT, fontSize: "11px", fontWeight: 700, padding: "3px 12px", borderRadius: "20px", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "18px" }}>Our Story</div>
              <h2 style={{ margin: "0 0 18px", fontSize: "28px", fontWeight: 900, color: "#ffffff", letterSpacing: "-0.5px", lineHeight: 1.25 }}>
                Bridging European KNX Technology with North America
              </h2>
              <p style={{ margin: "0 0 16px", fontSize: "14.5px", color: "#94a3b8", lineHeight: 1.75 }}>
                As a European-trained KNX Certified Professional (Partner No. 20248) now based in the United States, Briq-n-Braq was founded to bridge the gap between established European KNX technology and the growing North American smart building market.
              </p>
              <p style={{ margin: 0, fontSize: "14.5px", color: "#94a3b8", lineHeight: 1.75 }}>
                We serve professional installers and system integrators across the EU and North America through a zero-inventory drop-ship model, partnering directly with leading KNX manufacturers.
              </p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              {[
                { label: "KNX Partner No.",  value: "20248",            icon: "◉" },
                { label: "Markets",           value: "EU & North America", icon: "⬡" },
                { label: "Model",             value: "Zero-Inventory Drop-Ship", icon: "◇" },
                { label: "Standards",         value: "ETS6 · KNX TP · IEC 14543-3", icon: "▦" },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "14px", background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "14px 18px" }}>
                  <div style={{ width: "32px", height: "32px", borderRadius: "7px", background: `${ACCENT}20`, border: `1px solid ${ACCENT}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px", color: ACCENT, flexShrink: 0 }}>{item.icon}</div>
                  <div>
                    <div style={{ fontSize: "10px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "2px" }}>{item.label}</div>
                    <div style={{ fontSize: "13.5px", fontWeight: 700, color: "#0f1f3d" }}>{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Request Access */}
        <section id="bnb-request-access" style={{ background: "#ffffff", padding: "72px 6%", borderTop: "1px solid #e2e8f0" }}>
          <div style={{ maxWidth: "640px", margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: "36px" }}>
              <div style={{ display: "inline-block", background: "#fff7ed", border: "1px solid #F97316", color: "#c2410c", fontSize: "11px", fontWeight: 800, padding: "4px 14px", borderRadius: "20px", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "14px" }}>Portal Access</div>
              <h2 style={{ margin: "0 0 10px", fontSize: "28px", fontWeight: 900, color: "#0f1f3d", letterSpacing: "-0.5px" }}>Request Portal Access</h2>
              <p style={{ margin: 0, fontSize: "15px", color: "#64748b" }}>For KNX professional installers and system integrators</p>
            </div>

            {reqStatus === "success" ? (
              <div style={{ background: "#052e16", border: "1.5px solid #16a34a", borderRadius: "12px", padding: "32px", textAlign: "center" }}>
                <div style={{ fontSize: "32px", marginBottom: "12px" }}>✓</div>
                <div style={{ fontSize: "17px", fontWeight: 700, color: "#4ade80", marginBottom: "8px" }}>Request received!</div>
                <p style={{ margin: 0, fontSize: "14px", color: "#86efac", lineHeight: 1.65 }}>
                  Thank you! We'll review your request and be in touch within 24 hours.
                </p>
              </div>
            ) : (
              <form
                ref={reqFormRef}
                onSubmit={async (e) => {
                  e.preventDefault();
                  setReqStatus("sending");
                  try {
                    await emailjs.send(
                      "service_3ozc5bt",
                      "template_271fk8b",
                      {
                        first_name:    reqForm.firstName,
                        last_name:     reqForm.lastName,
                        company_name:  reqForm.company,
                        email:         reqForm.email,
                        country:       reqForm.country,
                        knx_certified: reqForm.knxCert === true ? "Yes" : reqForm.knxCert === false ? "No" : "Not specified",
                        message:       reqForm.message || "—",
                      },
                      "QTpa1XU6aYrcSHnTm"
                    );
                    setReqStatus("success");
                    setReqForm({ firstName: "", lastName: "", company: "", email: "", country: "", knxCert: null, message: "" });
                  } catch {
                    setReqStatus("error");
                  }
                }}
                style={{ background: "#ffffff", border: "1px solid #cbd5e1", borderTop: "4px solid #F97316", borderRadius: "12px", boxShadow: "0 4px 24px rgba(15,31,61,0.10)", padding: "36px 40px", display: "flex", flexDirection: "column", gap: "18px" }}
              >
                {/* Name row */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                  {[
                    { label: "First Name", key: "firstName", placeholder: "Jean" },
                    { label: "Last Name",  key: "lastName",  placeholder: "Dupont" },
                  ].map(({ label, key, placeholder }) => (
                    <div key={key}>
                      <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#0f1f3d", marginBottom: "6px" }}>{label} <span style={{ color: ACCENT }}>*</span></label>
                      <input
                        required
                        type="text"
                        placeholder={placeholder}
                        value={reqForm[key]}
                        onChange={e => { setReqForm(f => ({ ...f, [key]: e.target.value })); setReqStatus("idle"); }}
                        onFocus={e => e.currentTarget.style.border = "1.5px solid #F97316"}
                        onBlur={e => e.currentTarget.style.border = "1.5px solid #cbd5e1"}
                        style={{ width: "100%", boxSizing: "border-box", padding: "11px 14px", fontSize: "14px", background: "#f8fafc", border: "1.5px solid #cbd5e1", borderRadius: "8px", color: "#0f1f3d", fontFamily: "inherit", outline: "none" }}
                      />
                    </div>
                  ))}
                </div>

                {/* Company */}
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#0f1f3d", marginBottom: "6px" }}>Company Name <span style={{ color: ACCENT }}>*</span></label>
                  <input
                    required
                    type="text"
                    placeholder="Acme Automation GmbH"
                    value={reqForm.company}
                    onChange={e => { setReqForm(f => ({ ...f, company: e.target.value })); setReqStatus("idle"); }}
                    onFocus={e => e.currentTarget.style.border = "1.5px solid #F97316"}
                    onBlur={e => e.currentTarget.style.border = "1.5px solid #cbd5e1"}
                    style={{ width: "100%", boxSizing: "border-box", padding: "11px 14px", fontSize: "14px", background: "#f8fafc", border: "1.5px solid #cbd5e1", borderRadius: "8px", color: "#0f1f3d", fontFamily: "inherit", outline: "none" }}
                  />
                </div>

                {/* Email */}
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#0f1f3d", marginBottom: "6px" }}>Email Address <span style={{ color: ACCENT }}>*</span></label>
                  <input
                    required
                    type="email"
                    placeholder="you@company.com"
                    value={reqForm.email}
                    onChange={e => { setReqForm(f => ({ ...f, email: e.target.value })); setReqStatus("idle"); }}
                    onFocus={e => e.currentTarget.style.border = "1.5px solid #F97316"}
                    onBlur={e => e.currentTarget.style.border = "1.5px solid #cbd5e1"}
                    style={{ width: "100%", boxSizing: "border-box", padding: "11px 14px", fontSize: "14px", background: "#f8fafc", border: "1.5px solid #cbd5e1", borderRadius: "8px", color: "#0f1f3d", fontFamily: "inherit", outline: "none" }}
                  />
                </div>

                {/* Country */}
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#0f1f3d", marginBottom: "6px" }}>Country <span style={{ color: ACCENT }}>*</span></label>
                  <select
                    required
                    value={reqForm.country}
                    onChange={e => { setReqForm(f => ({ ...f, country: e.target.value })); setReqStatus("idle"); }}
                    onFocus={e => e.currentTarget.style.border = "1.5px solid #F97316"}
                    onBlur={e => e.currentTarget.style.border = "1.5px solid #cbd5e1"}
                    style={{ width: "100%", boxSizing: "border-box", padding: "11px 14px", fontSize: "14px", background: "#f8fafc", border: "1.5px solid #cbd5e1", borderRadius: "8px", color: reqForm.country ? "#0f1f3d" : "#94a3b8", fontFamily: "inherit", outline: "none", appearance: "none" }}
                  >
                    <option value="" disabled>Select your country…</option>
                    <optgroup label="North America">
                      <option>United States</option>
                      <option>Canada</option>
                    </optgroup>
                    <optgroup label="European Union">
                      {["Austria","Belgium","Bulgaria","Croatia","Cyprus","Czech Republic","Denmark","Estonia","Finland","France","Germany","Greece","Hungary","Ireland","Italy","Latvia","Lithuania","Luxembourg","Malta","Netherlands","Poland","Portugal","Romania","Slovakia","Slovenia","Spain","Sweden"].map(c => <option key={c}>{c}</option>)}
                    </optgroup>
                    <optgroup label="Other Europe">
                      {["Norway","Switzerland","United Kingdom"].map(c => <option key={c}>{c}</option>)}
                    </optgroup>
                  </select>
                </div>

                {/* KNX Certified toggle */}
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#0f1f3d", marginBottom: "8px" }}>KNX Certified?</label>
                  <div style={{ display: "flex", gap: "10px" }}>
                    {[{ label: "Yes", val: true }, { label: "No", val: false }].map(({ label, val }) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() => setReqForm(f => ({ ...f, knxCert: val }))}
                        style={{
                          flex: 1, padding: "10px", fontSize: "13px", fontWeight: 700,
                          borderRadius: "8px", cursor: "pointer",
                          background: reqForm.knxCert === val ? "#fff7ed" : "#f8fafc",
                          border: `1.5px solid ${reqForm.knxCert === val ? "#F97316" : "#cbd5e1"}`,
                          color: reqForm.knxCert === val ? "#c2410c" : "#0f1f3d",
                          transition: "all 0.12s",
                        }}
                      >{label}</button>
                    ))}
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#0f1f3d", marginBottom: "6px" }}>Message <span style={{ color: "#334155" }}>(optional)</span></label>
                  <textarea
                    rows={4}
                    placeholder="Tell us about your project or business…"
                    value={reqForm.message}
                    onChange={e => setReqForm(f => ({ ...f, message: e.target.value }))}
                    onFocus={e => e.currentTarget.style.border = "1.5px solid #F97316"}
                    onBlur={e => e.currentTarget.style.border = "1.5px solid #cbd5e1"}
                    style={{ width: "100%", boxSizing: "border-box", padding: "11px 14px", fontSize: "14px", background: "#f8fafc", border: "1.5px solid #cbd5e1", borderRadius: "8px", color: "#0f1f3d", fontFamily: "inherit", outline: "none", resize: "vertical" }}
                  />
                </div>

                {reqStatus === "error" && (
                  <div style={{ background: "#450a0a", border: "1px solid #991b1b", borderRadius: "8px", padding: "10px 14px", fontSize: "13px", color: "#fca5a5" }}>
                    Something went wrong. Please try again or email us directly at portal@briq-n-braq.com.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={reqStatus === "sending"}
                  style={{ background: reqStatus === "sending" ? "#7c3210" : ACCENT, color: "#fff", border: "none", borderRadius: "8px", padding: "14px", fontSize: "15px", fontWeight: 800, cursor: reqStatus === "sending" ? "not-allowed" : "pointer", fontFamily: "inherit", transition: "background 0.15s" }}
                >
                  {reqStatus === "sending" ? "Sending…" : "Request Access →"}
                </button>
              </form>
            )}
          </div>
        </section>

        {/* Footer */}
        <footer style={{ background: "#0f1f3d", borderTop: "1px solid #1e3a6e", padding: "28px 6%" }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{ background: ACCENT, color: "#fff", fontWeight: 900, fontSize: "13px", padding: "3px 8px", borderRadius: "5px" }}>BB</div>
              <span style={{ color: "#cbd5e1", fontWeight: 700, fontSize: "14px" }}>Briq-n-Braq</span>
            </div>
            <div style={{ display: "flex", gap: "20px", alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ color: "#475569", fontSize: "13px" }}>briq-n-braq.com</span>
              <span style={{ color: ACCENT, fontSize: "13px", fontWeight: 600 }}>portal.briq-n-braq.com</span>
            </div>
            <span style={{ color: "#334155", fontSize: "12px" }}>KNX Partner No. 20248 · ETS6 · KNX TP · IEC 14543-3</span>
          </div>
          <p style={{ maxWidth: "1100px", margin: "18px auto 0", textAlign: "center", color: "#64748b", fontSize: "11.5px", lineHeight: 1.6 }}>
            Briq-n-Braq works with leading KNX manufacturers to source and supply components to professional installers across EU and North America.
          </p>
        </footer>

      </div>
    );
  }

  if (!authed) {
    return (
      <div style={{
        minHeight: "100vh", background: NAVY,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        padding: "24px",
      }}>
        <div style={{ width: "100%", maxWidth: "400px" }}>

          {/* Brand */}
          <div style={{ textAlign: "center", marginBottom: "32px" }}>
            <div style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: "56px", height: "56px", borderRadius: "14px",
              background: ACCENT, marginBottom: "14px",
              fontWeight: 900, fontSize: "20px", color: "#fff", letterSpacing: "-0.5px",
            }}>BB</div>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: "20px", letterSpacing: "-0.3px" }}>Briq-n-Braq</div>
            <div style={{
              display: "inline-block", marginTop: "8px",
              background: `${ACCENT}22`, border: `1px solid ${ACCENT}55`,
              color: ACCENT, fontSize: "11px", fontWeight: 700,
              padding: "3px 12px", borderRadius: "20px",
              textTransform: "uppercase", letterSpacing: "0.07em",
            }}>Member Portal</div>
          </div>

          {/* Card */}
          <div style={{
            background: SURFACE, borderRadius: "14px",
            padding: "32px 36px",
            boxShadow: "0 24px 48px rgba(0,0,0,0.35)",
          }}>
            <h2 style={{ margin: "0 0 6px", fontSize: "18px", fontWeight: 800, color: TEXT }}>Sign in</h2>
            <p style={{ margin: "0 0 24px", fontSize: "13px", color: TEXT_DIM }}>
              Access your KNX supply portal
            </p>

            <form onSubmit={handleLogin} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: TEXT_MID, marginBottom: "6px" }}>
                  Email address
                </label>
                <input
                  type="email"
                  value={loginEmail}
                  onChange={e => { setLoginEmail(e.target.value); setLoginError(""); }}
                  placeholder="admin@briq-n-braq.com"
                  required
                  style={{
                    width: "100%", boxSizing: "border-box",
                    padding: "10px 13px", fontSize: "14px",
                    border: `1.5px solid ${loginError ? "#ef4444" : BORDER}`,
                    borderRadius: "8px", outline: "none",
                    background: SURFACE2, color: TEXT,
                    fontFamily: "inherit",
                  }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: TEXT_MID, marginBottom: "6px" }}>
                  Password
                </label>
                <input
                  type="password"
                  value={loginPass}
                  onChange={e => { setLoginPass(e.target.value); setLoginError(""); }}
                  placeholder="••••••••"
                  required
                  style={{
                    width: "100%", boxSizing: "border-box",
                    padding: "10px 13px", fontSize: "14px",
                    border: `1.5px solid ${loginError ? "#ef4444" : BORDER}`,
                    borderRadius: "8px", outline: "none",
                    background: SURFACE2, color: TEXT,
                    fontFamily: "inherit",
                  }}
                />
              </div>

              {loginError && (
                <div style={{
                  background: "#fef2f2", border: "1px solid #fecaca",
                  borderRadius: "7px", padding: "9px 13px",
                  fontSize: "13px", color: "#dc2626", fontWeight: 500,
                }}>
                  {loginError}
                </div>
              )}

              <button
                type="submit"
                style={{
                  marginTop: "4px",
                  background: ACCENT, color: "#fff",
                  border: "none", borderRadius: "8px",
                  padding: "11px", fontSize: "14px", fontWeight: 700,
                  cursor: "pointer", fontFamily: "inherit",
                }}
              >
                Sign in to Portal
              </button>
            </form>
          </div>

          <p style={{ textAlign: "center", marginTop: "20px", fontSize: "12px", color: "#475569" }}>
            KNX Partner No. <span style={{ color: ACCENT, fontWeight: 600 }}>20248</span> · ETS6 · KNX TP
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: "flex", height: "100vh",
      background: BG,
      fontFamily: "'Segoe UI', 'Helvetica Neue', sans-serif",
      color: TEXT, overflow: "hidden",
    }}>

      {/* SIDEBAR */}
      <div style={{
        width: sidebarOpen ? "230px" : "60px", flexShrink: 0,
        background: NAVY,
        borderRight: `1px solid ${NAVY_MID}`,
        display: "flex", flexDirection: "column",
        transition: "width 0.22s ease",
        overflow: "hidden",
      }}>
        {/* Logo */}
        <div style={{ padding: "18px 14px 16px", borderBottom: `1px solid ${NAVY_MID}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{
              width: "36px", height: "36px", flexShrink: 0,
              background: ACCENT,
              borderRadius: "8px",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "13px", fontWeight: 900, color: "#fff", letterSpacing: "-1px",
            }}>B·B</div>
            {sidebarOpen && (
              <div style={{ overflow: "hidden" }}>
                <div style={{ fontWeight: 800, fontSize: "15px", color: "#fff", letterSpacing: "0.3px", whiteSpace: "nowrap" }}>Briq-n-Braq</div>
                <div style={{ fontSize: "9px", color: "#94a3b8", letterSpacing: "2px", textTransform: "uppercase", marginTop: "1px" }}>KNX Supply Portal</div>
              </div>
            )}
          </div>
          {sidebarOpen && (
            <div style={{
              marginTop: "12px",
              background: NAVY_MID,
              border: `1px solid ${ACCENT_DIM}`,
              borderRadius: "6px",
              padding: "5px 10px",
            }}>
              <span style={{ fontSize: "9px", color: ACCENT_LIGHT, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase" }}>
                ✦ KNX Certified Partner
              </span>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav style={{ padding: "10px 8px", flex: 1 }}>
          {navItems.map(item => {
            const active = activeTab === item.id;
            return (
              <button key={item.id} onClick={() => setActiveTab(item.id)} style={{
                width: "100%", display: "flex", alignItems: "center",
                gap: "10px", padding: "9px 10px",
                borderRadius: "7px", border: "none",
                cursor: "pointer", marginBottom: "3px",
                background: active ? NAVY_MID : "transparent",
                color: active ? "#fff" : "#64748b",
                fontSize: "13px", fontWeight: active ? 700 : 500,
                textAlign: "left", transition: "all 0.12s",
                borderLeft: active ? `2px solid ${ACCENT}` : "2px solid transparent",
              }}>
                <span style={{ fontSize: "14px", flexShrink: 0, opacity: active ? 1 : 0.5 }}>{item.icon}</span>
                {sidebarOpen && <span style={{ whiteSpace: "nowrap" }}>{item.label}</span>}
              </button>
            );
          })}
        </nav>

        <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{
          margin: "10px 8px", padding: "8px",
          background: NAVY_MID, border: `1px solid ${NAVY_MID}`,
          borderRadius: "7px", color: "#64748b",
          cursor: "pointer", fontSize: "11px",
        }}>{sidebarOpen ? "← Collapse" : "→"}</button>
      </div>

      {/* MAIN */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Topbar */}
        <div style={{
          height: "54px", background: SURFACE,
          borderBottom: `1px solid ${BORDER}`,
          display: "flex", alignItems: "center",
          justifyContent: "space-between",
          padding: "0 24px", flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontWeight: 800, fontSize: "15px", color: TEXT, textTransform: "capitalize" }}>{activeTab}</span>
            <span style={{ color: TEXT_DIM }}>›</span>
            <span style={{ color: TEXT_DIM, fontSize: "12px" }}>May 2026</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{
              background: SURFACE2, border: `1px solid ${BORDER}`,
              borderRadius: "7px", padding: "5px 14px",
              fontSize: "12px", color: TEXT_DIM, cursor: "pointer",
            }}>⌕ Search parts…</div>
            <div style={{
              padding: "4px 10px",
              background: "#ffedd5", border: `1px solid ${ACCENT_DIM}`,
              borderRadius: "20px",
              fontSize: "10px", color: ACCENT_DIM, fontWeight: 700,
              letterSpacing: "1px", textTransform: "uppercase",
            }}>KNX ✦</div>
            <button onClick={() => setCurrency(c => c === "USD" ? "EUR" : "USD")} style={{
              background: currency === "EUR" ? "#dbeafe" : "#ffedd5",
              border: `1px solid ${currency === "EUR" ? "#93c5fd" : ACCENT_DIM}`,
              borderRadius: "7px", padding: "4px 11px",
              fontSize: "11px", fontWeight: 700,
              color: currency === "EUR" ? "#1e40af" : ACCENT_DIM,
              cursor: "pointer", fontFamily: "monospace", letterSpacing: "0.5px",
              transition: "all 0.15s",
            }}>{currency === "USD" ? "$ USD" : "€ EUR"}</button>
            <div style={{
              width: "32px", height: "32px", borderRadius: "50%",
              background: ACCENT,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 800, fontSize: "11px", color: "#fff",
            }}>BB</div>
            <button
              onClick={() => { setAuthed(false); setLoginEmail(""); setLoginPass(""); setShowLanding(true); }}
              style={{
                background: "none", border: `1px solid ${BORDER}`,
                borderRadius: "7px", padding: "4px 11px",
                fontSize: "11px", fontWeight: 600, color: TEXT_MID,
                cursor: "pointer", fontFamily: "inherit",
                transition: "border-color 0.15s, color 0.15s",
              }}
            >Sign out</button>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: "auto", padding: "24px", background: BG }}>

          {/* DASHBOARD */}
          {activeTab === "dashboard" && (
            <div>
              <div style={{ marginBottom: "22px" }}>
                <h1 style={{ fontSize: "20px", fontWeight: 900, color: TEXT, margin: 0 }}>KNX Supply Portal</h1>
                <p style={{ color: TEXT_DIM, fontSize: "12px", margin: "4px 0 0" }}>{new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })} · 11 suppliers active · EU &amp; NA markets</p>
                {renderDemoBanner(DEMO_TEXT_DASHBOARD)}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "14px", marginBottom: "20px" }}>
                {stats.map((s, i) => (
                  <div key={i} style={{
                    background: SURFACE, border: `1px solid ${BORDER}`,
                    borderTop: `3px solid ${ACCENT}`,
                    borderRadius: "10px", padding: "18px 16px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  }}>
                    <div style={{ fontSize: "10px", color: TEXT_DIM, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>{s.label}</div>
                    <div style={{ fontSize: "26px", fontWeight: 900, color: NAVY, letterSpacing: "-1px", lineHeight: 1 }}>{s.usd ? fmt(s.value) : s.value}</div>
                    <div style={{ marginTop: "7px", display: "flex", alignItems: "center", gap: "6px" }}>
                      <span style={{ fontSize: "12px", color: s.up ? "#16a34a" : "#dc2626", fontWeight: 700 }}>{s.delta}</span>
                      <span style={{ fontSize: "11px", color: TEXT_DIM }}>{s.sub}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1.7fr", gap: "14px", marginBottom: "20px" }}>
                <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: "10px", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                  <div style={{ fontSize: "10px", color: TEXT_DIM, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "4px" }}>Weekly Orders</div>
                  <div style={{ fontSize: "24px", fontWeight: 900, color: NAVY, marginBottom: "14px" }}>
                    44 <span style={{ fontSize: "12px", color: "#16a34a", fontWeight: 600 }}>↑ this week</span>
                  </div>
                  <MiniChart data={weeklyData} />
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: "7px" }}>
                    {dayLabels.map(l => <span key={l} style={{ fontSize: "9px", color: TEXT_DIM }}>{l}</span>)}
                  </div>
                </div>

                <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: "10px", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                  <div style={{ fontSize: "10px", color: TEXT_DIM, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "14px" }}>Top KNX Products</div>
                  {products.slice(0, 4).map((p, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                      <div style={{
                        width: "26px", height: "26px", background: "#ffedd5",
                        border: `1px solid ${ACCENT_DIM}`, borderRadius: "5px",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "10px", color: ACCENT_DIM, fontWeight: 800, flexShrink: 0,
                      }}>#{i + 1}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "12px", fontWeight: 600, color: TEXT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                        <div style={{ width: `${(p.sales / 215) * 100}%`, height: "3px", background: ACCENT, borderRadius: "2px", marginTop: "5px", opacity: 0.7 }} />
                      </div>
                      <div style={{ fontSize: "13px", fontWeight: 700, color: NAVY, flexShrink: 0 }}>{p.sales}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: "10px", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                  <div style={{ fontSize: "13px", fontWeight: 700, color: TEXT }}>Recent Orders</div>
                  <button onClick={() => setActiveTab("orders")} style={{ background: "none", border: "none", color: ACCENT, fontSize: "12px", cursor: "pointer", fontWeight: 600 }}>View all →</button>
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${BORDER}` }}>
                      {["Order", "Customer", "Product", "Status", "Total", "Profit"].map(h => (
                        <th key={h} style={{ textAlign: "left", padding: "0 0 9px", fontSize: "9px", color: TEXT_DIM, textTransform: "uppercase", letterSpacing: "1px", fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {orders.slice(0, 4).map((o, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${BORDER}` }}>
                        <td style={{ padding: "11px 0", fontSize: "11px", color: ACCENT_DIM, fontWeight: 700, fontFamily: "monospace" }}>{o.id}</td>
                        <td style={{ padding: "11px 0", fontSize: "12px", color: TEXT }}>{o.customer}</td>
                        <td style={{ padding: "11px 0", fontSize: "12px", color: TEXT_MID, maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.product}</td>
                        <td style={{ padding: "11px 0" }}><Badge status={o.status} /></td>
                        <td style={{ padding: "11px 0", fontSize: "13px", fontWeight: 700, color: NAVY }}>{fmt(o.total)}</td>
                        <td style={{ padding: "11px 0", fontSize: "12px", fontWeight: 600, color: "#16a34a" }}>{fmt(o.profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ORDERS */}
          {activeTab === "orders" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "22px" }}>
                <div>
                  <h1 style={{ fontSize: "20px", fontWeight: 900, color: TEXT, margin: 0 }}>Orders</h1>
                  <p style={{ color: TEXT_DIM, fontSize: "12px", margin: "4px 0 0" }}>Track and fulfil KNX part orders</p>
                </div>
                <button style={{
                  background: ACCENT, border: "none",
                  borderRadius: "8px", color: "#fff",
                  fontWeight: 700, fontSize: "13px",
                  padding: "9px 18px", cursor: "pointer",
                }} onClick={() => setModal("order")}>+ New Order</button>
              </div>
              <div style={{ marginTop: "-10px", marginBottom: "16px" }}>{renderDemoBanner(DEMO_TEXT_SHORT)}</div>
              <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: "10px", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${BORDER}`, background: SURFACE2 }}>
                      {["Order ID", "Customer", "Product", "Supplier", "Date", "Status", "Total", "Profit"].map(h => (
                        <th key={h} style={{ textAlign: "left", padding: "13px 14px", fontSize: "9px", color: TEXT_DIM, textTransform: "uppercase", letterSpacing: "1px", fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${BORDER}`, cursor: "pointer", transition: "background 0.1s" }}
                        onMouseEnter={e => e.currentTarget.style.background = SURFACE2}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <td style={{ padding: "13px 14px", fontSize: "11px", color: ACCENT_DIM, fontWeight: 700, fontFamily: "monospace" }}>{o.id}</td>
                        <td style={{ padding: "13px 14px", fontSize: "12px", color: TEXT, fontWeight: 600 }}>{o.customer}</td>
                        <td style={{ padding: "13px 14px", fontSize: "12px", color: TEXT_MID, maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.product}</td>
                        <td style={{ padding: "13px 14px", fontSize: "11px", color: TEXT_DIM }}>{o.supplier}</td>
                        <td style={{ padding: "13px 14px", fontSize: "11px", color: TEXT_DIM }}>{o.date}</td>
                        <td style={{ padding: "13px 14px" }}><Badge status={o.status} /></td>
                        <td style={{ padding: "13px 14px", fontSize: "13px", fontWeight: 700, color: NAVY }}>{fmt(o.total)}</td>
                        <td style={{ padding: "13px 14px", fontSize: "12px", fontWeight: 600, color: "#16a34a" }}>{fmt(o.profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* PRODUCTS */}
          {activeTab === "products" && renderProducts(false)}

          {/* SUPPLIERS */}
          {activeTab === "suppliers" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "22px" }}>
                <div>
                  <h1 style={{ fontSize: "20px", fontWeight: 900, color: TEXT, margin: 0 }}>Suppliers</h1>
                  <p style={{ color: TEXT_DIM, fontSize: "12px", margin: "4px 0 0" }}>KNX manufacturer relationships</p>
                </div>
                <button style={{
                  background: ACCENT, border: "none",
                  borderRadius: "8px", color: "#fff",
                  fontWeight: 700, fontSize: "13px",
                  padding: "9px 18px", cursor: "pointer",
                }} onClick={() => setModal("supplier")}>+ Add Supplier</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: "14px" }}>
                {suppliers.map((s, i) => (
                  <div key={i} style={{
                    background: SURFACE,
                    border: `1px solid ${BORDER}`,
                    borderTop: `3px solid ${s.cert ? ACCENT : BORDER}`,
                    borderRadius: "10px", padding: "18px",
                    cursor: "pointer", transition: "box-shadow 0.15s",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)"}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)"}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "14px" }}>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: "15px", color: NAVY }}>{s.name}</div>
                        <div style={{ fontSize: "11px", color: TEXT_DIM, marginTop: "2px" }}>{s.country}</div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "5px" }}>
                        <Badge status={s.status} />
                        {s.cert && (
                          <span style={{ fontSize: "9px", color: ACCENT_DIM, background: "#ffedd5", border: `1px solid ${ACCENT_DIM}`, borderRadius: "4px", padding: "2px 6px", letterSpacing: "0.8px", fontWeight: 700 }}>
                            ✦ KNX CERT
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                      {[
                        { label: "Products", value: s.products },
                        { label: "Avg. Ship", value: s.avgShip },
                        { label: "Orders",   value: s.orders },
                        { label: "Rating",   value: `★ ${s.rating}` },
                      ].map((st, j) => (
                        <div key={j} style={{ background: SURFACE2, borderRadius: "7px", padding: "9px 11px", border: `1px solid ${BORDER}` }}>
                          <div style={{ fontSize: "9px", color: TEXT_DIM, textTransform: "uppercase", letterSpacing: "0.8px" }}>{st.label}</div>
                          <div style={{ fontSize: "13px", fontWeight: 700, color: NAVY, marginTop: "2px" }}>{st.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ANALYTICS */}
          {activeTab === "analytics" && (
            <div>
              <div style={{ marginBottom: "22px" }}>
                <h1 style={{ fontSize: "20px", fontWeight: 900, color: TEXT, margin: 0 }}>Analytics</h1>
                <p style={{ color: TEXT_DIM, fontSize: "12px", margin: "4px 0 0" }}>KNX business performance · May 2026</p>
                {renderDemoBanner(DEMO_TEXT_SHORT)}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "14px", marginBottom: "20px" }}>
                {[
                  { label: "Total Revenue",   value: "$24,680",   usd: true,  note: "May 2026",     color: NAVY },
                  { label: "Total Profit",    value: "$7,503",    usd: true,  note: "30.4% margin", color: "#16a34a" },
                  { label: "Fulfilment Rate", value: "175 / 183", usd: false, note: "95.6% rate",   color: "#1e40af" },
                ].map((c, i) => (
                  <div key={i} style={{
                    background: SURFACE, border: `1px solid ${BORDER}`,
                    borderTop: `3px solid ${ACCENT}`,
                    borderRadius: "10px", padding: "22px 18px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  }}>
                    <div style={{ fontSize: "9px", color: TEXT_DIM, textTransform: "uppercase", letterSpacing: "1px" }}>{c.label}</div>
                    <div style={{ fontSize: "30px", fontWeight: 900, color: c.color, marginTop: "8px", letterSpacing: "-1px" }}>{c.usd ? fmt(c.value) : c.value}</div>
                    <div style={{ fontSize: "11px", color: TEXT_DIM, marginTop: "4px" }}>{c.note}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: "10px", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                  <div style={{ fontSize: "12px", fontWeight: 700, color: TEXT, marginBottom: "14px" }}>Orders by Supplier</div>
                  {suppliers.filter(s => s.status === "Active").map((s, i) => {
                    const total = suppliers.filter(x => x.status === "Active").reduce((a, b) => a + b.orders, 0);
                    const pct = Math.round((s.orders / total) * 100);
                    return (
                      <div key={i} style={{ marginBottom: "12px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                          <span style={{ fontSize: "11px", color: TEXT_MID }}>{s.name}</span>
                          <span style={{ fontSize: "11px", color: TEXT_DIM }}>{s.orders} ({pct}%)</span>
                        </div>
                        <div style={{ height: "5px", background: BORDER, borderRadius: "3px", overflow: "hidden" }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: ACCENT, borderRadius: "3px" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: "10px", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                  <div style={{ fontSize: "12px", fontWeight: 700, color: TEXT, marginBottom: "14px" }}>Margin by Category</div>
                  {[
                    { cat: "Inputs",     margin: 35.1 },
                    { cat: "Sensors",    margin: 32.0 },
                    { cat: "Actuators",  margin: 30.0 },
                    { cat: "Gateways",   margin: 29.9 },
                    { cat: "Interfaces", margin: 24.0 },
                  ].map((c, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "11px" }}>
                      <div style={{ width: "70px", fontSize: "10px", color: TEXT_MID, flexShrink: 0 }}>{c.cat}</div>
                      <div style={{ flex: 1, height: "5px", background: BORDER, borderRadius: "3px", overflow: "hidden" }}>
                        <div style={{ width: `${(c.margin / 40) * 100}%`, height: "100%", background: "#16a34a", borderRadius: "3px" }} />
                      </div>
                      <div style={{ fontSize: "12px", fontWeight: 700, color: "#16a34a", width: "38px", textAlign: "right" }}>{c.margin}%</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* RESOURCES */}
          {activeTab === "resources" && (
            <div style={{ margin: "-24px" }}>
              {/* Sub-nav */}
              <div style={{
                display: "flex", gap: "2px",
                padding: "12px 16px 0",
                background: "#162d52",
                borderBottom: "2px solid #253d6e",
              }}>
                {[
                  { id: "flowchart",       label: "Installation Flowchart" },
                  { id: "guide",          label: "Product Selection Guide" },
                  { id: "troubleshoot",   label: "Troubleshooting Guide" },
                  { id: "ets6ref",        label: "ETS6 Quick Reference" },
                  { id: "diagrams",       label: "System Diagrams" },
                  { id: "calculator",     label: "🧮 Project Calculator" },
                ].map(p => {
                  const active = resourcesPage === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setResourcesPage(p.id)}
                      style={{
                        background: active ? "#0f1f3d" : "transparent",
                        border: `1px solid ${active ? "#253d6e" : "transparent"}`,
                        borderBottom: active ? "2px solid #0f1f3d" : "2px solid transparent",
                        marginBottom: "-2px",
                        borderRadius: "6px 6px 0 0",
                        padding: "7px 16px",
                        color: active ? "#F97316" : "#94a3b8",
                        fontSize: "12.5px", fontWeight: active ? 700 : 500,
                        cursor: "pointer",
                        transition: "color 0.12s",
                      }}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
              {/* Page */}
              {resourcesPage === "flowchart"     && <KNXFlowchart />}
              {resourcesPage === "guide"         && <KNXProductGuide />}
              {resourcesPage === "troubleshoot"  && <KNXTroubleshootingGuide />}
              {resourcesPage === "ets6ref"       && <ETS6QuickReference />}
              {resourcesPage === "diagrams"      && <KNXDiagrams />}
              {resourcesPage === "calculator"    && <KNXProjectCalculator />}
            </div>
          )}

          {/* ABOUT */}
          {activeTab === "about" && (
            <div style={{ maxWidth: "860px" }}>

              {/* Hero */}
              <div style={{
                background: `linear-gradient(135deg, ${NAVY} 0%, #1e3a6e 100%)`,
                border: `1px solid #253d6e`,
                borderLeft: `4px solid ${ACCENT}`,
                borderRadius: "12px",
                padding: "32px 36px",
                marginBottom: "22px",
              }}>
                <div style={{
                  display: "inline-block",
                  background: `${ACCENT}22`,
                  border: `1px solid ${ACCENT}44`,
                  color: ACCENT,
                  fontSize: "11px",
                  fontWeight: 700,
                  padding: "3px 10px",
                  borderRadius: "4px",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  marginBottom: "14px",
                }}>
                  KNX Partner No. 20248
                </div>
                <h1 style={{
                  margin: "0 0 14px",
                  fontSize: "26px",
                  fontWeight: 900,
                  color: "#ffffff",
                  letterSpacing: "-0.5px",
                  lineHeight: 1.25,
                }}>
                  Bridging European KNX Technology<br />with North America
                </h1>
                <p style={{
                  margin: 0,
                  fontSize: "15px",
                  color: "#cbd5e1",
                  lineHeight: 1.7,
                  maxWidth: "620px",
                }}>
                  Premium KNX automation components sourced direct from leading European manufacturers — backed by the technical expertise of a certified KNX professional and senior IT specialist.
                </p>
              </div>

              {/* Our Story */}
              <div style={{
                background: SURFACE,
                border: `1px solid ${BORDER}`,
                borderTop: `3px solid ${ACCENT}`,
                borderRadius: "10px",
                padding: "24px 28px",
                marginBottom: "22px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
                  <div style={{
                    width: "32px", height: "32px", borderRadius: "8px",
                    background: `${ACCENT}18`, border: `1px solid ${ACCENT}44`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "15px", color: ACCENT, flexShrink: 0,
                  }}>◈</div>
                  <h2 style={{ margin: 0, fontSize: "16px", fontWeight: 800, color: TEXT }}>Our Story</h2>
                </div>
                <p style={{ margin: 0, fontSize: "14px", color: TEXT_MID, lineHeight: 1.75 }}>
                  As a European-trained KNX Certified Professional (Partner No. 20248) now based in the United States, Briq-n-Braq was founded to bridge the gap between established European KNX technology and the growing North American smart building market. We serve professional installers and system integrators across the EU and North America through a zero-inventory drop-ship model, partnering directly with leading KNX manufacturers.
                </p>
                <blockquote style={{ margin: "16px 0 0", padding: "12px 16px", background: `${ACCENT}10`, borderLeft: `4px solid ${ACCENT}`, borderRadius: "0 8px 8px 0", fontSize: "13.5px", fontStyle: "italic", color: TEXT_MID, lineHeight: 1.65 }}>
                  Briq-n-Braq works with leading KNX manufacturers to source and supply components to professional installers across EU and North America.
                </blockquote>
              </div>

              {/* Why Briq-n-Braq */}
              <div style={{ marginBottom: "22px" }}>
                <h2 style={{ margin: "0 0 14px", fontSize: "16px", fontWeight: 800, color: TEXT }}>Why Briq-n-Braq</h2>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  {[
                    {
                      icon: "◉",
                      title: "KNX Certified Expertise",
                      sub: "Partner No. 20248",
                      body: "Every product recommendation and technical query is handled by a KNX-certified professional with hands-on commissioning experience across residential and commercial installations.",
                    },
                    {
                      icon: "⬡",
                      title: "EU & North America",
                      sub: "Global reach, local knowledge",
                      body: "Deep roots in the European KNX ecosystem combined with an on-the-ground presence in North America — we understand the code requirements, voltages, and market needs of both regions.",
                    },
                    {
                      icon: "◇",
                      title: "Zero-Inventory Drop-Ship",
                      sub: "Direct from manufacturer",
                      body: "No warehouse overhead. Orders ship directly from certified KNX manufacturers to your project site, keeping costs lean and lead times predictable.",
                    },
                    {
                      icon: "▦",
                      title: "Senior IT & KNX Support",
                      sub: "End-to-end technical capability",
                      body: "From ETS6 project files and IP network architecture to BMS integration and remote access — our background in IT means we can support the full technology stack around your KNX installation.",
                    },
                  ].map((card, i) => (
                    <div key={i} style={{
                      background: SURFACE,
                      border: `1px solid ${BORDER}`,
                      borderRadius: "10px",
                      padding: "20px 22px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                        <div style={{
                          width: "34px", height: "34px", borderRadius: "8px",
                          background: `${ACCENT}18`, border: `1px solid ${ACCENT}44`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: "16px", color: ACCENT, flexShrink: 0,
                        }}>{card.icon}</div>
                        <div>
                          <div style={{ fontSize: "13.5px", fontWeight: 700, color: TEXT, lineHeight: 1.2 }}>{card.title}</div>
                          <div style={{ fontSize: "11px", color: ACCENT, fontWeight: 600, marginTop: "2px" }}>{card.sub}</div>
                        </div>
                      </div>
                      <p style={{ margin: 0, fontSize: "13px", color: TEXT_MID, lineHeight: 1.65 }}>{card.body}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mission */}
              <div style={{
                background: `linear-gradient(135deg, ${NAVY} 0%, #162d52 100%)`,
                border: `1px solid #253d6e`,
                borderRadius: "10px",
                padding: "22px 28px",
                marginBottom: "22px",
                display: "flex",
                alignItems: "flex-start",
                gap: "16px",
              }}>
                <div style={{
                  width: "36px", height: "36px", borderRadius: "8px",
                  background: `${ACCENT}22`, border: `1px solid ${ACCENT}55`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "17px", color: ACCENT, flexShrink: 0, marginTop: "1px",
                }}>◎</div>
                <div>
                  <div style={{ fontSize: "10px", color: ACCENT, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "8px" }}>
                    Our Mission
                  </div>
                  <p style={{ margin: 0, fontSize: "15px", color: "#e2e8f0", lineHeight: 1.7, fontStyle: "italic" }}>
                    "To make premium European KNX automation components accessible to North American installers and integrators, with the technical expertise to support every project."
                  </p>
                </div>
              </div>

              {/* Contact */}
              <div style={{
                background: SURFACE,
                border: `1px solid ${BORDER}`,
                borderTop: `3px solid ${ACCENT}`,
                borderRadius: "10px",
                padding: "22px 28px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}>
                <h2 style={{ margin: "0 0 14px", fontSize: "16px", fontWeight: 800, color: TEXT }}>Get in Touch</h2>
                <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                  {[
                    { label: "Installer Portal", value: "portal.briq-n-braq.com", icon: "▦" },
                    { label: "Main Website",     value: "briq-n-braq.com",        icon: "⬡" },
                  ].map((link, i) => (
                    <div key={i} style={{
                      display: "flex", alignItems: "center", gap: "12px",
                      background: SURFACE2,
                      border: `1px solid ${BORDER}`,
                      borderRadius: "8px",
                      padding: "12px 18px",
                      flex: 1, minWidth: "220px",
                    }}>
                      <div style={{
                        width: "32px", height: "32px", borderRadius: "7px",
                        background: `${ACCENT}18`, border: `1px solid ${ACCENT}44`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "14px", color: ACCENT, flexShrink: 0,
                      }}>{link.icon}</div>
                      <div>
                        <div style={{ fontSize: "10px", color: TEXT_DIM, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "2px" }}>{link.label}</div>
                        <div style={{ fontSize: "13.5px", fontWeight: 700, color: ACCENT }}>{link.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <p style={{ margin: "14px 0 0", fontSize: "12px", color: TEXT_DIM }}>
                  KNX Certified Partner No. <span style={{ color: TEXT, fontWeight: 600 }}>20248</span> · ETS6 · KNX TP · KNXnet/IP · IEC 14543-3
                </p>
              </div>

            </div>
          )}

        </div>
      </div>

      {modal === "supplier" && <AddSupplierModal onSave={saveSupplier} onClose={() => setModal(null)} />}
      {modal === "product"  && <AddProductModal  supplierNames={suppliers.map(s => s.name)} onSave={saveProduct} onClose={() => setModal(null)} />}
      {modal === "order"    && <AddOrderModal    productList={products} onSave={saveOrder} onClose={() => setModal(null)} />}

      {lightboxEl}
    </div>
  );
}
